import { type ModalComponentProps } from "../contexts/useModal";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Fieldset,
  Flex,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Mark,
  Modal,
  NumberInput,
  PasswordInput,
  ScrollArea,
  Select,
  Table,
  Tabs,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { ReactNode, use, useEffect, useRef, useState } from "react";
import { Dropzone } from "@mantine/dropzone";
import {
  IconCancel,
  IconCheck,
  IconFile,
  IconFileExcel,
  IconKeyboard,
  IconLock,
  IconMessageCircle,
  IconMinus,
  IconPhoto,
  IconRefresh,
  IconRegistered,
  IconSettings,
  IconUpload,
  IconUser,
  IconUserCircle,
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconPackage,
  IconInfoCircle,
  IconSearch,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import "@mantine/dropzone/styles.css";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import {
  cancelActionProps,
  dangerActionProps,
  labels,
  modeOnProps,
  modeOffProps,
  primaryActionProps,
  searchActionProps,
  secondaryActionProps,
  formPrimaryActionProps,
  SHOW_ALIAS,
} from "../constants/uiActions";
import {
  boardDateFormat,
  INT_MAX,
  lpad,
  setActionLog,
  formatDiagnosticError,
  formatTerminalFirmwareType,
} from "../utils/util";
import { RoleList, RoleListPermissions } from "../types/types";
import dayjs from "dayjs";
import ExcelJS from "exceljs";

type TerminalModelRow = {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  terminalColor?: string;
  useYn: string;
};

type RegisterSampleExportParams = {
  internalModelName: string;
  externalModelName: string;
  vanName: string;
  terminalColor: string;
  aliasName?: string;
  spSerialNoPrefix: string;
  spSerialNoDigitLength: number;
  /** 사용자가 입력한 시작 번호 (오름/내림 원본) */
  spSerialNoStartNumber: number;
  /** 사용자가 입력한 종료 번호 */
  spSerialNoEndNumber: number;
  productionQuantity: number;
  macPrefix: string;
  mac2StartNumber: number;
  mac2EndNumber: number;
  serialNoReverse: boolean;
  macAddressReverse: boolean;
  dikEnabled?: boolean;
  dikPairs?: DikPair[];
};

type DikPair = { key: string; value: string };

const DIK_KEY_MAX_LEN = 16;
const DIK_VALUE_MAX_LEN = 32;

const DEFAULT_MAC_PREFIX = "C4E7BE";

/** start→end 등록 시 실제 생성 기준(min) + 역순 여부 + 대수 */
const resolveNumberRange = (start: number, end: number) => {
  const reverse = start > end;
  return {
    baseStart: Math.min(start, end),
    quantity: Math.abs(end - start) + 1,
    reverse,
  };
};

const normalizeMacPrefix = (macPrefix: string) =>
  macPrefix.replace(/\s+/g, "").toUpperCase();

/** S/N·MAC 고정문자열: 공백·특수문자 제거 후 대문자 */
const sanitizeAlphanumericPrefix = (value: string) =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const sanitizeMacHexPrefix = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^0-9A-F]/g, "")
    .slice(0, 6);

const normalizeModelName = (value: string) => value.trim().toUpperCase();

const formatMac1Display = (macPrefix: string) => {
  const normalized = normalizeMacPrefix(macPrefix);
  return normalized.match(/.{1,2}/g)?.join(" ") ?? normalized;
};

const buildMacAddress = (macPrefix: string, mac2Number: number) =>
  normalizeMacPrefix(macPrefix) +
  mac2Number.toString(16).toUpperCase().padStart(6, "0");

const buildMac3 = (mac2Number: number) =>
  mac2Number.toString(16).toUpperCase().padStart(6, "0");

const normalizeDikHeader = (raw: string) =>
  raw.replace(/\s+/g, "").replace(/[/_]/g, "").toLowerCase();

const excelCellText = (cell: ExcelJS.Cell | undefined): string => {
  if (!cell) {
    return "";
  }
  const displayed = String(cell.text ?? "").trim();
  if (displayed) {
    return displayed;
  }
  const value = cell.value;
  if (value == null) {
    return "";
  }
  if (typeof value === "number") {
    return String(Math.trunc(value));
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return String(value).trim();
};

const isDikKeyHeader = (h: string) =>
  h === "보안키" ||
  h === "키" ||
  h === "파일명" ||
  h === "dik키" ||
  h === "dikkey" ||
  h === "diksn" ||
  h === "key";

const isDikValueHeader = (h: string) =>
  h === "보안키2" ||
  h === "값" ||
  h === "파일내용" ||
  h === "dik값" ||
  h === "dikvalue" ||
  h === "value";

const resolveDikColumnsFromHeader = (
  header: ExcelJS.Row,
): { keyCol: number; valueCol: number } | null => {
  let keyCol = -1;
  let valueCol = -1;
  header.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const h = normalizeDikHeader(excelCellText(cell));
    if (isDikKeyHeader(h)) {
      keyCol = colNumber;
    } else if (isDikValueHeader(h)) {
      valueCol = colNumber;
    }
  });
  if (keyCol > 0 && valueCol > 0 && keyCol !== valueCol) {
    return { keyCol, valueCol };
  }
  const c0 = normalizeDikHeader(excelCellText(header.getCell(1)));
  if (c0 === "번호" || c0 === "no" || c0 === "#") {
    return { keyCol: 2, valueCol: 3 };
  }
  const joined = [
    excelCellText(header.getCell(1)),
    excelCellText(header.getCell(2)),
    excelCellText(header.getCell(3)),
  ]
    .join("")
    .toLowerCase();
  if (
    joined.includes("키") ||
    joined.includes("key") ||
    joined.includes("dik") ||
    joined.includes("보안") ||
    joined.includes("값") ||
    joined.includes("value") ||
    joined.includes("파일명") ||
    joined.includes("파일내용") ||
    joined.includes("파일")
  ) {
    return { keyCol: 1, valueCol: 2 };
  }
  return null;
};

const looksLikeDikData = (key: string, value: string) =>
  /^[0-9A-Fa-f]{8,16}$/.test(key) && /^[0-9A-Fa-f]{16,32}$/.test(value);

const isHeaderLikePair = (key: string, value: string) => {
  const k = normalizeDikHeader(key);
  const v = normalizeDikHeader(value);
  return (
    isDikKeyHeader(k) ||
    isDikValueHeader(k) ||
    isDikKeyHeader(v) ||
    isDikValueHeader(v) ||
    k.includes("파일명") ||
    v.includes("파일내용") ||
    k.includes("보안키") ||
    v.includes("보안키")
  );
};
const parseDikExcelFile = async (file: File): Promise<DikPair[]> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return [];
  }
  let keyCol = 1;
  let valueCol = 2;
  const first = sheet.getRow(1);
  const headerCols = resolveDikColumnsFromHeader(first);
  if (headerCols) {
    keyCol = headerCols.keyCol;
    valueCol = headerCols.valueCol;
  }
  const pairs: DikPair[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    const key = excelCellText(row.getCell(keyCol));
    const value = excelCellText(row.getCell(valueCol));
    if (!key && !value) {
      return;
    }
    if (isHeaderLikePair(key, value) || !looksLikeDikData(key, value)) {
      return;
    }
    if (key.length > DIK_KEY_MAX_LEN) {
      throw new Error(
        `DIK 키 길이는 ${DIK_KEY_MAX_LEN}자를 넘을 수 없습니다. (${rowNumber}행)`,
      );
    }
    if (value.length > DIK_VALUE_MAX_LEN) {
      throw new Error(
        `DIK 값 길이는 ${DIK_VALUE_MAX_LEN}자를 넘을 수 없습니다. (${rowNumber}행)`,
      );
    }
    pairs.push({ key, value });
  });
  return pairs;
};

const dikCountMismatchMessage = (qty: number, dikCount: number) =>
  `DIK 개수가 생산 대수와 맞지 않습니다. [생산: ${qty}대, DIK: ${dikCount}개]`;

const buildRegisterExcelBaseFilename = (params: RegisterSampleExportParams) => {
  const timestamp = dayjs().format("YYYYMMDD_HHmmss");
  const cleanAlias = (params.aliasName || "").replace(/[\\/:*?"<>|]/g, "_").trim();
  const modelName = (params.externalModelName || params.internalModelName || "단말기").replace(/[\\/:*?"<>|]/g, "_").trim();
  const count = params.productionQuantity;
  return cleanAlias
    ? `단말기등록_${cleanAlias}_${modelName}_${count}대_${timestamp}`
    : `단말기등록_${modelName}_${count}대_${timestamp}`;
};

const downloadRegisterSampleExcel = async (
  params: RegisterSampleExportParams,
  options?: { baseFilename?: string; duplicateIndex?: number },
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");
  const withDik = Boolean(params.dikEnabled && params.dikPairs?.length);

  const columns: Partial<ExcelJS.Column>[] = [
    { header: "번호", key: "number", width: 8 },
    { header: "S/N", key: "spSerialNo", width: 18 },
    { header: "MAC", key: "macAddress", width: 18 },
    { header: "MAC-2", key: "mac2", width: 10 },
  ];
  if (withDik) {
    columns.push(
      { header: "보안키", key: "securityKeyRef1", width: 20 },
      { header: "보안키2", key: "securityKeyRef2", width: 36 },
    );
  }
  columns.push(
    { header: "내부모델", key: "internalModelName", width: 14 },
    { header: "외부모델", key: "externalModelName", width: 14 },
    { header: "VAN", key: "vanName", width: 12 },
    { header: "색상", key: "terminalColor", width: 10 },
  );
  worksheet.columns = columns;

  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "76933C" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };
  });

  const spRange = resolveNumberRange(
    params.spSerialNoStartNumber,
    params.spSerialNoEndNumber,
  );
  const macRange = resolveNumberRange(
    params.mac2StartNumber,
    params.mac2EndNumber,
  );
  const colCount = columns.length;

  for (let i = 0; i < params.productionQuantity; i++) {
    const serialIndex = spRange.reverse ? spRange.quantity - 1 - i : i;
    const macIndex = macRange.reverse ? macRange.quantity - 1 - i : i;
    const spNumber = spRange.baseStart + serialIndex;
    const mac2Number = macRange.baseStart + macIndex;
    const dik = params.dikPairs?.[i];
    const rowValues: Record<string, string | number> = {
      number: i + 1,
      spSerialNo:
        params.spSerialNoPrefix +
        lpad(String(spNumber), params.spSerialNoDigitLength, "0"),
      macAddress: buildMacAddress(params.macPrefix, mac2Number),
      mac2: mac2Number,
      internalModelName: params.internalModelName,
      externalModelName: params.externalModelName,
      vanName: params.vanName,
      terminalColor: params.terminalColor,
    };
    if (withDik) {
      rowValues.securityKeyRef1 = dik?.key ?? "";
      rowValues.securityKeyRef2 = dik?.value ?? "";
    }
    const dataRow = worksheet.addRow(rowValues);

    const isStripedRow = i % 2 === 0;
    for (let col = 1; col <= colCount; col++) {
      const cell = dataRow.getCell(col);
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "DDEBF7" } },
        left: { style: "thin", color: { argb: "DDEBF7" } },
        bottom: { style: "thin", color: { argb: "DDEBF7" } },
        right: { style: "thin", color: { argb: "DDEBF7" } },
      };
      if (isStripedRow) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEBF1DE" },
        };
      }
    }
  }

  const baseFilename =
    options?.baseFilename ?? buildRegisterExcelBaseFilename(params);
  const duplicateIndex = options?.duplicateIndex ?? 0;
  const filename =
    duplicateIndex > 0
      ? `${baseFilename} (${duplicateIndex}).xlsx`
      : `${baseFilename}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export type ErrorAlertProps = ModalComponentProps<boolean> & {
  content: string;
};
export type ConfirmProps = ModalComponentProps<boolean> & {
  title: string;
  content: string;
};
export type TerminalsExcelImportProps = ModalComponentProps<boolean> & {
  initialTab?: "excel" | "typing";
  initialMode?: "register" | "update" | "incoming";
};
export type WorkerUserGenerateProps = ModalComponentProps<boolean> & {};
export type WorkerUserRejectProps = ModalComponentProps<boolean> & {
  userNoList: number[];
  userData?: Array<{ userNo: number; userId: string; userName: string }>;
};
export type UserGenerateProps = ModalComponentProps<boolean> & {};
export type TerminalModelGenerateProps = ModalComponentProps<boolean> & {};
export type TerminalModelUpdateProps = ModalComponentProps<boolean> & {
  row: {
    terminalModelId: number;
    internalModelName: string;
    externalModelName: string;
    dikYn?: string;
    serialCableCount?: number;
    useYn: string;
  };
};
export type WorkerUserGenerateToAdminProps = ModalComponentProps<boolean> & {};
export type UserUpdateProps = ModalComponentProps<boolean> & {
  row: any;
  workerYn: string;
};
export type RoleAddProps = ModalComponentProps<boolean> & {};
export type RoleUpdateProps = ModalComponentProps<boolean> & {
  row: any;
};
export type PasswordChangeProps = ModalComponentProps<boolean> & {};
export type TerminalViewProps = ModalComponentProps<boolean> & {
  row: any;
};
export type ActionLogDetailProps = ModalComponentProps<boolean> & {
  row: any;
};
export type ActionLogConfigModalProps = ModalComponentProps<boolean> & {};
export type ErrorCodeGuideModalProps = ModalComponentProps<boolean> & {};
export type TerminalDeleteProps = ModalComponentProps<boolean> & {};
export type TerminalIncomingProps = ModalComponentProps<boolean> & {};
export type TerminalUpdateProps = ModalComponentProps<boolean> & {};

export function ErrorAlert({ content, onConfirm, onCancel }: ErrorAlertProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={"Error"}>
      <div>{content}</div>
      <Flex justify={"flex-end"}>
        <button className="btn" onClick={onCancel}>
          확인
        </button>
      </Flex>
    </Modal>
  );
}

export function Confirm({ title, content, onConfirm, onCancel }: ConfirmProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={title}>
      <Text size="md" pb={8}>
        {content}
      </Text>
      <Flex justify={"space-between"} align={"center"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          {...cancelActionProps}
        >
          {labels.cancel}
        </Button>

        <Button
          leftSection={<IconCheck size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(true);
          }}
          {...primaryActionProps}
        >
          {labels.confirm}
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalsExcelImport({
  onConfirm,
  onCancel,
  initialMode = "register",
}: TerminalsExcelImportProps) {
  const [mode, setMode] = useState<"register" | "update" | "incoming" | null>(
    initialMode,
  ); // 등록/수정/반품 모드 선택
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // 엑셀 탭 사용 중지 → 직접입력만 사용
  const [tab, setTab] = useState<string | null>("typing");
  const [isValidate, setIsValidate] = useState(false);

  const theme = useMantineTheme();
  const [terminalModelId, setTerminalModelId] = useState("");
  const [terminalModels, setTerminalModels] = useState<TerminalModelRow[]>([]);
  const [vanType, setVanType] = useState("");
  const [terminalColor, setTerminalColor] = useState("");
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [aliasName, setAliasName] = useState("");
  const [spSerialNoPrefix, setSpSerialNoPrefix] = useState("");
  const [spSerialNoDigitLength, setSpSerialNoDigitLength] = useState<
    number | ""
  >("");
  const [spSerialNoStartNumber, setSpSerialNoStartNumber] = useState<
    number | ""
  >("");
  const [spSerialNoEndNumber, setSpSerialNoEndNumber] = useState<number | "">(
    "",
  );
  const [macPrefix, setMacPrefix] = useState(DEFAULT_MAC_PREFIX);
  const [mac2StartNumber, setMac2StartNumber] = useState<number | "">("");
  const [mac2EndNumber, setMac2EndNumber] = useState<number | "">("");
  const pendingRegisterExportRef = useRef<RegisterSampleExportParams | null>(
    null,
  );
  const pendingCreateBatchRequestRef = useRef<string | null>(null);
  const registerExcelBaseFilenameRef = useRef<string | null>(null);
  const registerExcelRedownloadCountRef = useRef(0);
  const submitCreateBatchLockRef = useRef(false);
  const [registerConfirmStep, setRegisterConfirmStep] = useState(false);
  const [registerConfirmKeyword, setRegisterConfirmKeyword] = useState("");
  const [registerPreviewParams, setRegisterPreviewParams] =
    useState<RegisterSampleExportParams | null>(null);
  const [dikEnabled, setDikEnabled] = useState(false);
  const [dikFile, setDikFile] = useState<File | null>(null);
  const [dikPairs, setDikPairs] = useState<DikPair[]>([]);
  const pendingDikFileRef = useRef<File | null>(null);

  const hasSpDigitLength =
    typeof spSerialNoDigitLength === "number" && spSerialNoDigitLength > 0;
  const hasSpStartNumber = typeof spSerialNoStartNumber === "number";
  const hasSpEndNumber = typeof spSerialNoEndNumber === "number";
  const hasMac2StartNumber = typeof mac2StartNumber === "number";
  const hasMac2EndNumber = typeof mac2EndNumber === "number";

  const spRangeInfo =
    hasSpStartNumber && hasSpEndNumber
      ? resolveNumberRange(spSerialNoStartNumber, spSerialNoEndNumber)
      : null;
  const macRangeInfo =
    hasMac2StartNumber && hasMac2EndNumber
      ? resolveNumberRange(mac2StartNumber, mac2EndNumber)
      : null;
  const productionQuantity = spRangeInfo?.quantity ?? null;
  const hasProductionQuantity =
    typeof productionQuantity === "number" && productionQuantity > 0;
  const rangeCountsMatch =
    spRangeInfo != null &&
    macRangeInfo != null &&
    spRangeInfo.quantity === macRangeInfo.quantity;

  const formatSpSerialPreview = (serialNumber: number | null) => {
    if (
      !spSerialNoPrefix.trim() ||
      !hasSpDigitLength ||
      serialNumber === null
    ) {
      return "-";
    }
    return (
      spSerialNoPrefix + lpad(String(serialNumber), spSerialNoDigitLength, "0")
    );
  };

  const formatMacPreview = (mac2: number | null) => {
    if (!macPrefix.trim() || mac2 === null) {
      return "-";
    }
    return buildMacAddress(macPrefix, mac2);
  };

  const terminalModelOptions = terminalModels.map((model) => {
    const vanLabel =
      vanTypeOptions.find((option) => option.value === String(model.vanType))
        ?.label ?? String(model.vanType);
    return {
      value: String(model.terminalModelId),
      label: `${model.internalModelName} / ${model.externalModelName} (${vanLabel})`,
    };
  });

  const selectedTerminalModel = terminalModels.find(
    (model) => String(model.terminalModelId) === terminalModelId,
  );

  useEffect(() => {
    // 모드가 선택되어야 하고, 파일이나 정보가 입력되어야 함
    if (!mode) {
      setIsValidate(false);
      return;
    }

    if (tab === "excel" && excelFile) {
      setIsValidate(true);
    } else if (
      tab === "typing" &&
      terminalModelId !== "" &&
      hasProductionQuantity &&
      rangeCountsMatch &&
      spSerialNoPrefix.trim() !== "" &&
      hasSpDigitLength &&
      hasSpStartNumber &&
      hasSpEndNumber &&
      macPrefix.length === 6 &&
      hasMac2StartNumber &&
      hasMac2EndNumber &&
      (mode !== "register" || !dikEnabled || dikFile != null)
    ) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [
    mode,
    tab,
    excelFile,
    terminalModelId,
    productionQuantity,
    rangeCountsMatch,
    aliasName,
    spSerialNoPrefix,
    spSerialNoDigitLength,
    spSerialNoStartNumber,
    spSerialNoEndNumber,
    macPrefix,
    mac2StartNumber,
    mac2EndNumber,
    dikEnabled,
    dikFile,
  ]);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/model/list", {
        params: { currentPageNo: 1, recordsPerPage: INT_MAX },
      })
      .then((res) => {
        const list = (res.data?.data?.list ?? []).filter(
          (item: TerminalModelRow) =>
            String(item.useYn || "").toUpperCase() === "Y",
        );
        setTerminalModels(list);
      })
      .catch((err) => {
        console.log(err);
        setTerminalModels([]);
      });
    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setVanTypeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
        setVanTypeOptions([]);
      });
  }, []);

  const syncModelDerivedFields = (nextId: string) => {
    if (!nextId) {
      setVanType("");
      setTerminalColor("");
      return;
    }
    const model = terminalModels.find(
      (item) => String(item.terminalModelId) === nextId,
    );
    if (!model) {
      setVanType("");
      setTerminalColor("");
      return;
    }
    const van = vanTypeOptions.find(
      (option) => option.value === String(model.vanType),
    );
    setVanType(van?.label || "");
    setTerminalColor(model.terminalColor || "");
  };

  const handleTerminalModelChange = (value: string | null) => {
    const nextId = value || "";
    setTerminalModelId(nextId);
    syncModelDerivedFields(nextId);
  };

  useEffect(() => {
    syncModelDerivedFields(terminalModelId);
  }, [terminalModelId, terminalModels, vanTypeOptions]);

  useEffect(() => {
    setRegisterConfirmStep(false);
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(null);
    pendingCreateBatchRequestRef.current = null;
    pendingRegisterExportRef.current = null;
    registerExcelBaseFilenameRef.current = null;
    registerExcelRedownloadCountRef.current = 0;
    pendingDikFileRef.current = null;
    setDikEnabled(false);
    setDikFile(null);
    setDikPairs([]);
  }, [tab, mode]);

  const buildTypingRequestPayload = (): {
    exportParams: RegisterSampleExportParams;
    requestJson: string;
  } | null => {
    if (
      !terminalModelId ||
      !spRangeInfo ||
      !macRangeInfo ||
      !hasSpDigitLength ||
      !selectedTerminalModel ||
      spRangeInfo.quantity !== macRangeInfo.quantity
    ) {
      return null;
    }

    const normalizedSpPrefix = sanitizeAlphanumericPrefix(spSerialNoPrefix);
    const normalizedMacPrefix = sanitizeMacHexPrefix(macPrefix);
    const normalizedColor = terminalColor.trim().toUpperCase();
    const normalizedAlias = aliasName.trim().replace(/\s+/g, " ");
    const qty = spRangeInfo.quantity;
    const digitLength = spSerialNoDigitLength as number;
    const spUserStart = spSerialNoStartNumber as number;
    const spUserEnd = spSerialNoEndNumber as number;
    const macUserStart = mac2StartNumber as number;
    const macUserEnd = mac2EndNumber as number;

    return {
      exportParams: {
        internalModelName: selectedTerminalModel.internalModelName,
        externalModelName: selectedTerminalModel.externalModelName,
        vanName: vanType,
        terminalColor: normalizedColor,
        aliasName: normalizedAlias || undefined,
        spSerialNoPrefix: normalizedSpPrefix,
        spSerialNoDigitLength: digitLength,
        spSerialNoStartNumber: spUserStart,
        spSerialNoEndNumber: spUserEnd,
        productionQuantity: qty,
        macPrefix: normalizedMacPrefix,
        mac2StartNumber: macUserStart,
        mac2EndNumber: macUserEnd,
        serialNoReverse: spRangeInfo.reverse,
        macAddressReverse: macRangeInfo.reverse,
        dikEnabled: mode === "register" && dikEnabled,
        dikPairs: mode === "register" && dikEnabled ? dikPairs : undefined,
      },
      // 백엔드: min 시작번호 + reverse + quantity
      requestJson: JSON.stringify({
        terminalModelId: Number(terminalModelId),
        terminalColor: normalizedColor,
        productionQuantity: qty,
        spSerialNoPrefix: normalizedSpPrefix,
        spSerialNoDigitLength: digitLength,
        spSerialNoStartNumber: spRangeInfo.baseStart,
        spSerialNoEndNumber: Math.max(spUserStart, spUserEnd),
        macPrefix: normalizedMacPrefix,
        mac2StartNumber: macRangeInfo.baseStart,
        serialNoReverse: spRangeInfo.reverse,
        macAddressReverse: macRangeInfo.reverse,
        aliasName: normalizedAlias || undefined,
        update: mode === "update",
        incoming: mode === "incoming",
        dikEnabled: mode === "register" && dikEnabled,
      }),
    };
  };

  const submitCreateBatch = async (requestJson: string, dikExcel?: File | null) => {
    if (submitCreateBatchLockRef.current) {
      return;
    }
    submitCreateBatchLockRef.current = true;
    const form = new FormData();
    form.append("request", requestJson);
    if (dikExcel) {
      form.append("dikFile", dikExcel);
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 API호출",
      });
      const res = await axiosInstance.post(
        "/api/v1/terminal/create-batch",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.code === "200") {
        const groupId = res.data.data.terminalImportGroupId;
        try {
          localStorage.setItem("terminalImportGroupId", String(groupId));
        } catch { }
        const intervalId = setInterval(async () => {
          try {
            const progressResp = await axiosInstance.get(
              `/api/v1/terminal/imports/${groupId}`,
            );
            if (progressResp.data?.data.status === "FAILED") {
              const { displayMsg, logContent } = formatDiagnosticError(
                progressResp.data?.data,
                "단말기 일괄 등록에 실패했습니다."
              );
              await setActionLog({
                eventType: "IMPORT",
                title: "단말기 일괄 등록 실패",
                content: logContent,
              });
              toast.error(displayMsg);
              setError(displayMsg);
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch { }
              clearInterval(intervalId);
              setUploading(false);
              submitCreateBatchLockRef.current = false;
              return;
            }

            const percent = progressResp.data?.data.progress ?? 1;
            setProgress(percent);
            if (percent >= 100) {
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch { }
              clearInterval(intervalId);
              setUploading(false);
              onConfirm?.(true);
            }
          } catch (err: unknown) {
            const { displayMsg, logContent } = formatDiagnosticError(
              err,
              "단말기 등록 상태 확인 중 오류가 발생했습니다."
            );
            toast.error(displayMsg);
            setError(displayMsg);
            await setActionLog({
              eventType: "IMPORT",
              title: "단말기 등록 상태 확인 실패",
              content: logContent,
            });
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch { }
            clearInterval(intervalId);
            setUploading(false);
            submitCreateBatchLockRef.current = false;
          }
        }, 500);
      } else {
        const { displayMsg, logContent } = formatDiagnosticError(
          res.data,
          "단말기 등록 요청에 실패했습니다."
        );
        toast.error(displayMsg);
        setError(displayMsg);
        await setActionLog({
          eventType: "IMPORT",
          title: "단말기 등록 요청 실패",
          content: logContent,
        });
        setUploading(false);
        submitCreateBatchLockRef.current = false;
      }
    } catch (e: any) {
      const { displayMsg, logContent } = formatDiagnosticError(
        e,
        "단말기 등록 요청 중 통신 오류가 발생했습니다."
      );
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 등록 요청 실패",
        content: logContent,
      });
      toast.error(displayMsg);
      setError(displayMsg);
      setUploading(false);
      submitCreateBatchLockRef.current = false;
    }
  };

  const handleRegisterPreview = async () => {
    const payload = buildTypingRequestPayload();
    if (!payload) {
      if (
        spRangeInfo &&
        macRangeInfo &&
        spRangeInfo.quantity !== macRangeInfo.quantity
      ) {
        toast.error("S/N과 MAC 대수가 일치해야 합니다.");
      } else {
        toast.error("모델과 S/N·MAC 시작/종료번호를 모두 입력해주세요.");
      }
      return;
    }

    let exportParams = payload.exportParams;
    if (dikEnabled) {
      if (!dikFile) {
        toast.error("DIK 엑셀 파일을 업로드해주세요.");
        return;
      }
      let pairs: DikPair[];
      try {
        pairs = await parseDikExcelFile(dikFile);
      } catch (parseErr) {
        const msg =
          parseErr instanceof Error
            ? parseErr.message
            : "DIK 엑셀을 읽을 수 없습니다.";
        toast.error(msg);
        return;
      }
      if (pairs.length !== exportParams.productionQuantity) {
        toast.error(
          dikCountMismatchMessage(
            exportParams.productionQuantity,
            pairs.length,
          ),
        );
        return;
      }
      setDikPairs(pairs);
      exportParams = {
        ...exportParams,
        dikEnabled: true,
        dikPairs: pairs,
      };
    } else {
      setDikPairs([]);
      exportParams = {
        ...exportParams,
        dikEnabled: false,
        dikPairs: undefined,
      };
    }

    const baseFilename = buildRegisterExcelBaseFilename(exportParams);
    registerExcelBaseFilenameRef.current = baseFilename;
    registerExcelRedownloadCountRef.current = 0;

    try {
      await downloadRegisterSampleExcel(exportParams, { baseFilename });
    } catch (exportErr) {
      console.error(exportErr);
      toast.error("등록 엑셀 다운로드에 실패하였습니다.");
      return;
    }

    pendingRegisterExportRef.current = exportParams;
    pendingCreateBatchRequestRef.current = payload.requestJson;
    pendingDikFileRef.current = dikEnabled ? dikFile : null;
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(exportParams);
    setRegisterConfirmStep(true);
    setError(null);
  };

  const handleConfirmRegister = async () => {
    if (submitCreateBatchLockRef.current || uploading) {
      return;
    }
    const requestJson = pendingCreateBatchRequestRef.current;
    if (!requestJson) {
      toast.error("등록 정보가 없습니다. 다시 시도해주세요.");
      setRegisterConfirmStep(false);
      return;
    }

    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 직접입력 등록 확인",
    });
    await submitCreateBatch(requestJson, pendingDikFileRef.current);
  };

  const handleCancelRegisterConfirm = () => {
    setRegisterConfirmStep(false);
    setRegisterConfirmKeyword("");
    setRegisterPreviewParams(null);
    pendingCreateBatchRequestRef.current = null;
    pendingRegisterExportRef.current = null;
    pendingDikFileRef.current = null;
    registerExcelBaseFilenameRef.current = null;
    registerExcelRedownloadCountRef.current = 0;
    setError(null);
  };

  const handleRedownloadRegisterExcel = async () => {
    const exportParams = pendingRegisterExportRef.current;
    const baseFilename = registerExcelBaseFilenameRef.current;
    if (!exportParams || !baseFilename) {
      return;
    }
    registerExcelRedownloadCountRef.current += 1;
    try {
      await downloadRegisterSampleExcel(exportParams, {
        baseFilename,
        duplicateIndex: registerExcelRedownloadCountRef.current,
      });
    } catch (exportErr) {
      console.error(exportErr);
      toast.error("등록 엑셀 다운로드에 실패하였습니다.");
      registerExcelRedownloadCountRef.current -= 1;
    }
  };

  const handleDikExcelDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const isExcel =
      file.type === "application/vnd.ms-excel" ||
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      /\.xlsx?$/i.test(file.name);
    if (!isExcel) {
      const msg = "엑셀 파일만 업로드 가능합니다.";
      setError(msg);
      toast.error(msg);
      setDikFile(null);
      setDikPairs([]);
      return;
    }
    try {
      const pairs = await parseDikExcelFile(file);
      setDikFile(file);
      setDikPairs(pairs);
      setError(null);
      toast.dismiss();
      if (
        hasProductionQuantity &&
        pairs.length !== productionQuantity
      ) {
        toast.error(
          dikCountMismatchMessage(productionQuantity as number, pairs.length),
        );
      }
    } catch (parseErr) {
      const msg =
        parseErr instanceof Error
          ? parseErr.message
          : "DIK 엑셀을 읽을 수 없습니다.";
      toast.error(msg);
      setError(msg);
      setDikFile(null);
      setDikPairs([]);
    }
  };

  const handleExcelDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (
      file.type === "application/vnd.ms-excel" ||
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setExcelFile(file);
      setError(null);
      toast.dismiss();
    } else {
      const msg = "엑셀 파일만 업로드 가능합니다.";
      setError(msg);
      toast.error(msg);
      setExcelFile(null);
    }
  };

  const handleUpload = async () => {
    if (!mode) {
      toast.error("등록 또는 수정, 반품 모드를 선택해주세요.");
      return;
    }

    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기정보 업로드 모달창/업로드 버튼 클릭",
    });

    if (tab === "typing" && mode === "register") {
      await handleRegisterPreview();
      return;
    }

    const form = new FormData();
    let url = "/api/v1/terminal";

    if (tab === "excel") {
      if (!excelFile) {
        const msg = "엑셀 파일을 업로드 해주세요.";
        toast.error(msg);
        return;
      }
      form.append("excelFile", excelFile);
      form.append("update", mode === "update" ? "true" : "false");
      form.append("incoming", mode === "incoming" ? "true" : "false");
      url += "/imports";
    } else {
      const payload = buildTypingRequestPayload();
      if (!payload) {
        if (
          spRangeInfo &&
          macRangeInfo &&
          spRangeInfo.quantity !== macRangeInfo.quantity
        ) {
          toast.error("S/N과 MAC 대수가 일치해야 합니다.");
        } else {
          toast.error("모델과 S/N·MAC 시작/종료번호를 모두 입력해주세요.");
        }
        return;
      }
      await submitCreateBatch(payload.requestJson);
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 API호출",
      });
      const res = await axiosInstance.post(url, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.code === "200") {
        const groupId = res.data.data.terminalImportGroupId;
        try {
          localStorage.setItem("terminalImportGroupId", String(groupId));
        } catch { }
        const intervalId = setInterval(async () => {
          try {
            const progressResp = await axiosInstance.get(
              `/api/v1/terminal/imports/${groupId}`,
            );
            if (progressResp.data?.data.status === "FAILED") {
              const { displayMsg, logContent } = formatDiagnosticError(
                progressResp.data?.data,
                "단말기 엑셀 업로드에 실패했습니다."
              );
              await setActionLog({
                eventType: "IMPORT",
                title: "단말기 엑셀 업로드 실패",
                content: logContent,
              });
              toast.error(displayMsg);
              setError(displayMsg);
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch { }
              clearInterval(intervalId);
              setUploading(false);
              submitCreateBatchLockRef.current = false;
              return;
            }

            const percent = progressResp.data?.data.progress ?? 1;
            setProgress(percent);
            if (percent >= 100) {
              try {
                localStorage.removeItem("terminalImportGroupId");
              } catch { }
              clearInterval(intervalId);
              setUploading(false);
              submitCreateBatchLockRef.current = false;
              onConfirm?.(true);
            }
          } catch (err: unknown) {
            const { displayMsg, logContent } = formatDiagnosticError(
              err,
              "단말기 업로드 상태 확인 중 오류가 발생했습니다."
            );
            toast.error(displayMsg);
            setError(displayMsg);
            await setActionLog({
              eventType: "IMPORT",
              title: "단말기 업로드 상태 확인 실패",
              content: logContent,
            });
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch { }
            clearInterval(intervalId);
            setUploading(false);
            submitCreateBatchLockRef.current = false;
          }
        }, 500);
      } else {
        const { displayMsg, logContent } = formatDiagnosticError(
          res.data,
          "단말기 업로드 요청에 실패했습니다."
        );
        toast.error(displayMsg);
        setError(displayMsg);
        await setActionLog({
          eventType: "IMPORT",
          title: "단말기 업로드 요청 실패",
          content: logContent,
        });
        setUploading(false);
        submitCreateBatchLockRef.current = false;
      }
    } catch (e: any) {
      const { displayMsg, logContent } = formatDiagnosticError(
        e,
        "단말기 업로드 요청 중 통신 오류가 발생했습니다."
      );
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 업로드 요청 실패",
        content: logContent,
      });
      toast.error(displayMsg);
      setError(displayMsg);
      setUploading(false);
      submitCreateBatchLockRef.current = false;
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={registerConfirmStep ? "등록 확인" : "단말기 등록/수정"}
      size={registerConfirmStep || tab !== "excel" ? "xl" : "md"}
    >
      {registerConfirmStep && registerPreviewParams ? (
        <>
          <Text mb="md">
            등록 엑셀이 다운로드되었습니다. 내용을 확인한 후 등록을
            진행해주세요.
          </Text>
          <Fieldset legend="등록 요약" mb="md">
            <Text size="sm">
              모델: {registerPreviewParams.internalModelName} /{" "}
              {registerPreviewParams.externalModelName}
            </Text>
            <Text size="sm">VAN: {registerPreviewParams.vanName}</Text>
            <Text size="sm">색상: {registerPreviewParams.terminalColor}</Text>
            {SHOW_ALIAS && registerPreviewParams.aliasName && (
              <Text size="sm">Note: {registerPreviewParams.aliasName}</Text>
            )}
            <Text size="sm">
              생산 대수: {registerPreviewParams.productionQuantity}대
            </Text>
            <Text size="sm">
              S/N:{" "}
              {`${registerPreviewParams.spSerialNoPrefix}${lpad(
                String(registerPreviewParams.spSerialNoStartNumber),
                registerPreviewParams.spSerialNoDigitLength,
                "0",
              )} ~ ${registerPreviewParams.spSerialNoPrefix}${lpad(
                String(registerPreviewParams.spSerialNoEndNumber),
                registerPreviewParams.spSerialNoDigitLength,
                "0",
              )}${registerPreviewParams.serialNoReverse ? " (내림차순)" : " (오름차순)"}`}
            </Text>
            <Text size="sm">
              MAC:{" "}
              {`${buildMacAddress(
                registerPreviewParams.macPrefix,
                registerPreviewParams.mac2StartNumber,
              )} ~ ${buildMacAddress(
                registerPreviewParams.macPrefix,
                registerPreviewParams.mac2EndNumber,
              )}${registerPreviewParams.macAddressReverse ? " (내림차순)" : " (오름차순)"}`}
            </Text>
            <Text size="sm">
              DIK:{" "}
              {registerPreviewParams.dikEnabled
                ? `포함 (${registerPreviewParams.dikPairs?.length ?? 0}건) · 보안키/보안키2`
                : "없음"}
            </Text>
          </Fieldset>
          <Button
            variant="light"
            leftSection={<IconFileExcel size={16} />}
            onClick={handleRedownloadRegisterExcel}
            mb="md"
            disabled={uploading}
          >
            엑셀 다시 받기
          </Button>
        </>
      ) : (
        <>
          {/* 등록/수정 모드 선택 */}
          <Group mb="md" gap="xs">
            <Button
              {...(mode === "register" ? modeOnProps : modeOffProps)}
              onClick={() => setMode("register")}
            >
              {labels.register}
            </Button>
            {/* <Button
              variant={mode === "update" ? "filled" : "outline"}
              style={
                mode === "update"
                  ? { backgroundColor: "#5B8FA8", color: "#fff" }
                  : { borderColor: "#5B8FA8", color: "#5B8FA8" }
              }
              onClick={() => setMode("update")}
              size="sm"
            >
              수정
            </Button>
            <Button
              variant={mode === "incoming" ? "filled" : "outline"}
              style={
                mode === "incoming"
                  ? { backgroundColor: "#a85b5b", color: "#fff" }
                  : { borderColor: "#a85b5b", color: "#a85b5b" }
              }
              onClick={() => setMode("incoming")}
              size="sm"
            >
              반품
            </Button> */}
          </Group>

          <Tabs defaultValue={tab} onChange={setTab}>
            <Tabs.List>
              {/* 사용 중지: 엑셀 탭
              <Tabs.Tab value="excel" leftSection={<IconFileExcel size={12} />}>
                엑셀
              </Tabs.Tab>
              */}
              <Tabs.Tab value="typing" leftSection={<IconKeyboard size={12} />}>
                직접입력
              </Tabs.Tab>
            </Tabs.List>
            {/* 사용 중지: 엑셀 탭 패널
            <Tabs.Panel value="excel" pt={"1rem"}>
              <Dropzone
                mb={16}
                multiple={false}
                onDrop={handleExcelDrop}
                maxSize={10 * 1024 ** 2}
                accept={[
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ]}
                disabled={uploading}
              >
                <Group
                  justify="center"
                  gap="xl"
                  mih={80}
                  style={{ pointerEvents: "none" }}
                >
                  <Text size="lg">
                    {excelFile
                      ? `${excelFile.name} (${Math.round(
                        excelFile.size / 1024,
                      )} KB)`
                      : "엑셀 파일을 드래그하거나 클릭해서 업로드"}
                  </Text>
                </Group>
              </Dropzone>
            </Tabs.Panel>
            */}
            <Tabs.Panel value="typing" pt={"1rem"}>
              <>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={4}>
                    <Select
                      label="모델"
                      placeholder="사용 중인 단말기 모델 선택"
                      data={terminalModelOptions}
                      value={terminalModelId || null}
                      onChange={handleTerminalModelChange}
                      clearable={false}
                      allowDeselect={false}
                      searchable
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="VAN"
                      value={vanType}
                      readOnly
                      placeholder="모델 선택 시 자동 설정"
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="색상"
                      value={terminalColor}
                      readOnly
                      placeholder="모델 선택 시 자동 설정"
                    />
                  </Grid.Col>
                </Grid>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={SHOW_ALIAS ? 4 : 12}>
                    <TextInput
                      label="생산 대수"
                      value={
                        hasProductionQuantity
                          ? `${productionQuantity}`
                          : ""
                      }
                      readOnly
                      placeholder="시작·종료번호로 자동 계산"
                    />
                  </Grid.Col>
                  {SHOW_ALIAS && (
                    <Grid.Col span={8}>
                      <TextInput
                        label="별칭"
                        placeholder="등록 구분을 위한 별칭 입력 (예: 1차 생산분)"
                        value={aliasName}
                        onChange={(e) => setAliasName(e.target.value)}
                      />
                    </Grid.Col>
                  )}
                </Grid>
                <Grid pb={"0.5rem"}>
                  <Grid.Col span={6}>
                    <Fieldset legend="S/N" pb={"0.5rem"}>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={6}>
                          <TextInput
                            label="고정문자열"
                            placeholder="예: PD2606"
                            value={spSerialNoPrefix}
                            onChange={(e) => {
                              setSpSerialNoPrefix(
                                sanitizeAlphanumericPrefix(e.target.value),
                              );
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="자릿수"
                            value={spSerialNoDigitLength}
                            min={1}
                            onChange={(value) =>
                              setSpSerialNoDigitLength(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={12}>
                          <NumberInput
                            label="시작번호"
                            value={spSerialNoStartNumber}
                            min={0}
                            onChange={(value) =>
                              setSpSerialNoStartNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={12}>
                          <NumberInput
                            label="종료번호"
                            value={spSerialNoEndNumber}
                            min={0}
                            onChange={(value) =>
                              setSpSerialNoEndNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Text>{`시작 : ${formatSpSerialPreview(
                        hasSpStartNumber ? spSerialNoStartNumber : null,
                      )}`}</Text>
                      <Text>{`종료 : ${formatSpSerialPreview(
                        hasSpEndNumber ? spSerialNoEndNumber : null,
                      )}`}</Text>
                      <Text>{`총 : ${hasProductionQuantity ? productionQuantity : "-"
                        }개`}</Text>
                      {spRangeInfo?.reverse && (
                        <Text size="sm" c="blue">
                          등록 순서: 시작번호가 종료번호보다 커서
                          내림차순으로 등록됩니다.
                        </Text>
                      )}
                    </Fieldset>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Fieldset legend="MAC address" pb={"0.5rem"}>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={12}>
                          <TextInput
                            label="MAC-1 (고정문자열)"
                            placeholder="예: C4E7BE"
                            maxLength={6}
                            value={macPrefix}
                            onChange={(e) => {
                              setMacPrefix(
                                sanitizeMacHexPrefix(e.target.value),
                              );
                            }}
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid pb={"0.5rem"}>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="MAC-2 시작번호"
                            value={mac2StartNumber}
                            min={0}
                            onChange={(value) =>
                              setMac2StartNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <NumberInput
                            label="MAC-2 종료번호"
                            value={mac2EndNumber}
                            min={0}
                            onChange={(value) =>
                              setMac2EndNumber(
                                typeof value === "number" ? value : "",
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>
                      <Text>{`시작 MAC : ${formatMacPreview(
                        hasMac2StartNumber ? mac2StartNumber : null,
                      )}`}</Text>
                      <Text>{`종료 MAC : ${formatMacPreview(
                        hasMac2EndNumber ? mac2EndNumber : null,
                      )}`}</Text>
                      <Text>{`총 : ${macRangeInfo ? macRangeInfo.quantity : "-"
                        }개`}</Text>
                      {macRangeInfo?.reverse && (
                        <Text size="sm" c="violet">
                          등록 순서: 시작번호가 종료번호보다 커서
                          내림차순으로 등록됩니다.
                        </Text>
                      )}
                      {spRangeInfo &&
                        macRangeInfo &&
                        spRangeInfo.quantity !== macRangeInfo.quantity && (
                          <Text size="sm" c="red" mt={4}>
                            S/N과 MAC 대수(
                            {spRangeInfo.quantity} / {macRangeInfo.quantity})
                            가 일치해야 합니다.
                          </Text>
                        )}
                      <Text size="xs" c="dimmed" mt={4}>
                        MAC-2 번호는 10진수이며, MAC-3(6자리 hex)로 변환되어 MAC
                        주소가 생성됩니다.
                      </Text>
                    </Fieldset>
                  </Grid.Col>
                </Grid>
                {mode === "register" && (
                  <Fieldset legend="DIK" mb="sm">
                    <Checkbox
                      label="DIK 포함"
                      checked={dikEnabled}
                      onChange={(e) => {
                        const on = e.currentTarget.checked;
                        setDikEnabled(on);
                        if (!on) {
                          setDikFile(null);
                          setDikPairs([]);
                        }
                      }}
                      mb="sm"
                    />
                    {dikEnabled && (
                      <>
                        <Dropzone
                          mb={8}
                          multiple={false}
                          onDrop={(files) => void handleDikExcelDrop(files)}
                          maxSize={10 * 1024 ** 2}
                          accept={[
                            "application/vnd.ms-excel",
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                          ]}
                          disabled={uploading}
                        >
                          <Group
                            justify="center"
                            gap="xl"
                            mih={56}
                            style={{ pointerEvents: "none" }}
                          >
                            <Text size="sm">
                              {dikFile
                                ? `${dikFile.name} (${dikPairs.length}건)`
                                : "DIK 엑셀을 드래그하거나 클릭해서 업로드 (키, 값)"}
                            </Text>
                          </Group>
                        </Dropzone>
                        {hasProductionQuantity &&
                          dikPairs.length > 0 &&
                          dikPairs.length !== productionQuantity && (
                            <Text size="sm" c="red">
                              {dikCountMismatchMessage(
                                productionQuantity as number,
                                dikPairs.length,
                              )}
                            </Text>
                          )}
                        <Text size="xs" c="dimmed">
                          S/N·MAC 엑셀 순서 1행부터 DIK 1번째를 붙입니다. 건수는
                          생산 대수와 같아야 합니다.
                        </Text>
                      </>
                    )}
                  </Fieldset>
                )}
              </>
            </Tabs.Panel>
          </Tabs>
        </>
      )}

      {uploading && (
        <Group gap="xs" mb="xs">
          <Loader size="sm" />
          <Text size="sm">업로드 중... {progress}%</Text>
        </Group>
      )}

      {error && (
        <Text size="sm" c="red" mb="xs">
          {error}
        </Text>
      )}

      {registerConfirmStep ? (
        <Flex justify="space-between" align="center">
          <Button
            variant="default"
            leftSection={<IconX size={20} stroke={1.5} />}
            onClick={handleCancelRegisterConfirm}
            disabled={uploading}
          >
            취소
          </Button>
          <Group align="center" gap="sm" justify="flex-end" wrap="nowrap">
            <Text size="sm" style={{ whiteSpace: "nowrap" }}>
              아래 입력란에 「OK」를 입력한 후 확인 버튼을 클릭해주세요.
            </Text>
            <TextInput
              placeholder="OK"
              value={registerConfirmKeyword}
              onChange={(e) =>
                setRegisterConfirmKeyword(e.target.value.toUpperCase())
              }
              disabled={uploading}
              w={100}
            />
            <Button
              {...primaryActionProps}
              leftSection={<IconCheck size={20} stroke={1.5} />}
              onClick={handleConfirmRegister}
              loading={uploading}
              disabled={registerConfirmKeyword.trim() !== "OK" || uploading}
            >
              {uploading ? "등록 중..." : labels.confirm}
            </Button>
          </Group>
        </Flex>
      ) : (
        <Flex justify="space-between">
          <Button
            {...(mode === "update" ? secondaryActionProps : primaryActionProps)}
            leftSection={<IconUpload size={20} stroke={1.5} />}
            onClick={handleUpload}
            loading={uploading}
            disabled={!isValidate || uploading}
          >
            {uploading
              ? mode === "update"
                ? "수정 중..."
                : mode === "incoming"
                  ? "반품 중..."
                  : "등록 중..."
              : mode === "update"
                ? labels.edit
                : mode === "incoming"
                  ? "반품"
                  : labels.register}
          </Button>
          <Button
            {...cancelActionProps}
            leftSection={<IconX size={20} stroke={1.5} />}
            onClick={onCancel}
          >
            {labels.cancel}
          </Button>
        </Flex>
      )}
    </Modal>
  );
}

export function WorkerUserGenerate({
  onConfirm,
  onCancel,
}: WorkerUserGenerateProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    if (isUniqueuserId && password && userName) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user/worker", {
        userId: userId,
        password: password,
        userName: userName,
        workerYn: "Y",
      });
      if (res.data.code === "200") {
        toast.success("작업자 등록 성공");
        onConfirm?.(true);
      } else {
        toast.error(res.data.msg || "작업자 등록 실패");
      }
    } catch (err) {
      toast.error("작업자 등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"작업자 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function WorkerUserReject({
  userNoList,
  userData,
  onConfirm,
  onCancel,
}: WorkerUserRejectProps) {
  const [rejectText, setRejectText] = useState("");
  const [rejectTextError, setRejectTextError] = useState<string | null>(null);
  const theme = useMantineTheme();

  const handleReject = async () => {
    if (rejectText.trim() === "") {
      setRejectTextError("반려사유를 입력해주세요.");
      return;
    }

    try {
      const res = await axiosInstance.delete("/api/v1/user/delete", {
        data: userNoList,
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "작업자 계정 반려 완료",
          content: { rejectText, userNoList, userData },
        });
        toast.success("반려 작업이 완료되었습니다.");
        onConfirm?.(true);
      } else {
        await setActionLog({
          eventType: "DELETE",
          title: "작업자 계정 반려 실패",
          content: { rejectText, userNoList, userData },
        });
        toast.error("반려 작업에 실패하였습니다.");
      }
    } catch (err) {
      await setActionLog({
        eventType: "DELETE",
        title: "작업자 계정 반려 실패",
        content: { err, userNoList, userData },
      });
      toast.error("반려 작업에 실패하였습니다.");
    }
  };
  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"작업자 반려"}
      closeOnClickOutside={false}
    >
      <Text size="md" pb={8}>
        총 {userNoList.length}명의 작업자를{" "}
        <Text span c="red" inherit>
          반려
        </Text>
        처리 하시겠습니까?
      </Text>
      <Text size="sm" pb={16} c={"red"}>
        반려 시 반려사유를 작성해야 합니다.
      </Text>
      <TextInput
        pb={16}
        label="반려사유"
        placeholder="반려사유를 입력해주세요."
        value={rejectText}
        onChange={(e) => {
          setRejectText(e.target.value);
        }}
        error={rejectTextError}
      />
      <Flex>
        <Button
          mr={"auto"}
          variant="filled"
          color="red"
          leftSection={
            <IconMinus size={20} color={theme.colors.blue[0]} stroke={1.5} />
          }
          onClick={handleReject}
        >
          반려
        </Button>
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={
            <IconX size={20} color={theme.colors.dark[9]} stroke={1.5} />
          }
        >
          취소
        </Button>
      </Flex>
    </Modal>
  );
}

export function WorkerUserGenerateToAdmin({
  onConfirm,
  onCancel,
}: WorkerUserGenerateToAdminProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  // const [orgOptions, setOrgOptions] = useState<
  //   { value: string; label: string }[]
  // >([]);
  // const [orgCode, setOrgCode] = useState("");

  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: "Y",
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          value: item.roles,
          label: item.roleName,
        }));

        const withDefault = [{ value: "", label: "권한선택" }, ...mapped];
        setRoleCodeOptions(withDefault);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([{ value: "", label: "권한선택" }]);
      });
  }, []);

  // useEffect(() => {
  //   const roleBased = roleCodeOptions.filter((opt) => opt.value !== "");
  //   const withDefault = [
  //     { value: "", label: "조직선택" },
  //     { value: "AA", label: "AA" },
  //     { value: "BB", label: "BB" },
  //     ...roleBased,
  //   ];
  //   setOrgOptions(withDefault);
  // }, [roleCodeOptions]);

  useEffect(() => {
    if (isUniqueuserId && password && userName && roleCode) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName, roleCode]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user", {
        userId: userId,
        password: password,
        userName: userName,
        roleCode: roleCode,
        // orgCode: orgCode,
        workerYn: "Y",
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "CREATE",
          title: "작업자 계정 등록 성공",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data.msg || "작업자 계정등록 실패");
        await setActionLog({
          eventType: "CREATE",
          title: "작업자 계정 등록 실패",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "CREATE",
        title: "작업자 계정 등록 실패",
        content: err,
      });
      toast.error("작업자 계정등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"유저 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      {/*
      <Select
        label="조직"
        mt="md"
        size="lg"
        withAsterisk
        data={orgOptions}
        defaultValue={orgCode}
        onChange={(_value) => setOrgCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      */}
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function UserGenerate({ onConfirm, onCancel }: UserGenerateProps) {
  const [userId, setUserId] = useState("");
  const [isUniqueuserId, setIsUniqueuserId] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  // const [orgOptions, setOrgOptions] = useState<
  //   { value: string; label: string }[]
  // >([]);
  // const [orgCode, setOrgCode] = useState("");
  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [password2Error, setPassword2Error] = useState<string | null>(null);
  const [userNameError, setUserNameError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: "N",
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          value: item.roles,
          label: item.roleName,
        }));

        const withDefault = [{ value: "", label: "권한선택" }, ...mapped];
        setRoleCodeOptions(withDefault);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([{ value: "", label: "권한선택" }]);
      });
  }, []);

  // useEffect(() => {
  //   const roleBased = roleCodeOptions.filter((opt) => opt.value !== "");
  //   const withDefault = [
  //     { value: "", label: "조직선택" },
  //     { value: "AA", label: "AA" },
  //     { value: "BB", label: "BB" },
  //     ...roleBased,
  //   ];
  //   setOrgOptions(withDefault);
  // }, [roleCodeOptions]);

  useEffect(() => {
    if (isUniqueuserId && password && userName && roleCode) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [isUniqueuserId, password, userName, roleCode]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const validateUserId = async (value: string) => {
    setIsUniqueuserId(false);
    try {
      const res = await axiosInstance.get(`/api/v1/user/check/${value}`);
      if (res.data.code === "200") {
        setIsUniqueuserId(true);
      } else {
        setUserIdError("이미 사용중인 아이디 입니다.");
      }
    } catch (err) {
      setUserIdError("이미 사용중인 아이디 입니다.");
      setIsValidate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUniqueuserId(false);
    setUserIdError(null);
    const value = e.target.value;
    setUserId(value);

    if (value.length < 3) {
      setUserIdError("아이디는 최소 3글자 이상이여야합니다.");
    } else {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateUserId(value);
      }, 500);
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/user", {
        userId: userId,
        password: password,
        userName: userName,
        roleCode: roleCode,
        workerYn: "N",
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "CREATE",
          title: "유저등록 성공",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        onConfirm?.(true);
      } else {
        await setActionLog({
          eventType: "CREATE",
          title: "유저등록 실패",
          content: JSON.stringify({ userId, userName, roleCode }),
        });
        toast.error(res.data.msg || "유저등록 실패");
      }
    } catch (err) {
      await setActionLog({
        eventType: "CREATE",
        title: "유저등록 실패",
        content: err,
      });
      toast.error("유저등록 중 오류 발생");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"유저 등록"}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={userId}
        onChange={handleUserId}
        disabled={loading}
        error={userIdError}
      />
      <PasswordInput
        label="비밀번호"
        mt="md"
        size="lg"
        placeholder="비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      {/*
      <Select
        label="조직"
        mt="md"
        size="lg"
        withAsterisk
        data={orgOptions}
        defaultValue={orgCode}
        onChange={(_value) => setOrgCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      */}
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        등록
      </Button>
    </Modal>
  );
}

export function TerminalModelGenerate({
  onConfirm,
  onCancel,
}: TerminalModelGenerateProps) {
  const [internalModelName, setInternalModelName] = useState("");
  const [externalModelName, setExternalModelName] = useState("");
  const [serialKeyInjectType, setSerialKeyInjectType] = useState<string>("");
  const [vanType, setVanType] = useState<string>("");
  const [terminalColor, setTerminalColor] = useState("WHITE");
  const [dikYn, setDikYn] = useState<string>("N");
  const [serialCableCount, setSerialCableCount] = useState<number | "">(1);
  const [loading, setLoading] = useState(false);
  const [injectTypeOptions, setInjectTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const isValid =
    internalModelName.trim() !== "" &&
    externalModelName.trim() !== "" &&
    serialKeyInjectType !== "" &&
    vanType !== "" &&
    terminalColor !== "" &&
    serialCableCount !== "" &&
    Number(serialCableCount) >= 0;

  useEffect(() => {
    Promise.all([
      axiosInstance.get("/api/v1/common/serial-key-inject-type"),
      axiosInstance.get("/api/v1/common/van"),
    ])
      .then(([injectRes, vanRes]) => {
        if (
          injectRes.data?.code === "200" &&
          Array.isArray(injectRes.data.data)
        ) {
          const opts = injectRes.data.data.map(
            (item: { code: string; name: string }) => ({
              value: item.code,
              label: `${item.code}: ${item.name}`,
            }),
          );
          setInjectTypeOptions(opts);
          if (opts.length > 0) {
            setSerialKeyInjectType(opts[0].value);
          }
        }
        if (vanRes.data?.code === "200" && Array.isArray(vanRes.data.data)) {
          const opts = vanRes.data.data.map(
            (item: { code: string; name: string }) => ({
              value: item.code,
              label: item.name,
            }),
          );
          setVanTypeOptions(opts);
        }
      })
      .catch(() => {
        toast.error("공통 코드 검색에 실패하였습니다.");
      });
  }, []);

  const handleRegister = async () => {
    if (!isValid) {
      toast.error("필수 항목을 모두 입력하세요.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/v1/terminal/model", {
        internalModelName: normalizeModelName(internalModelName),
        externalModelName: normalizeModelName(externalModelName),
        serialKeyInjectType: Number(serialKeyInjectType),
        vanType: Number(vanType),
        terminalColor,
        dikYn,
        serialCableCount: Number(serialCableCount) || 1,
      });
      if (res.data?.code === "200" || res.status === 202) {
        await setActionLog({
          eventType: "CREATE",
          title: "단말기 모델 등록 성공",
          content: {
            internalModelName,
            externalModelName,
            serialKeyInjectType,
            vanType,
            dikYn,
            serialCableCount,
          },
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data?.msg || "등록에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "등록에 실패하였습니다.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title="단말기 모델 등록"
      closeOnClickOutside={false}
    >
      <TextInput
        label="내부모델"
        mt="md"
        size="lg"
        placeholder="예: SPC-402"
        withAsterisk
        value={internalModelName}
        onChange={(e) =>
          setInternalModelName(normalizeModelName(e.currentTarget.value))
        }
        disabled={loading}
      />
      <TextInput
        label="외부모델"
        mt="md"
        size="lg"
        placeholder="예: SCS-4200"
        withAsterisk
        value={externalModelName}
        onChange={(e) =>
          setExternalModelName(normalizeModelName(e.currentTarget.value))
        }
        disabled={loading}
      />
      <Select
        label="시리얼 키 주입 타입"
        mt="md"
        size="lg"
        withAsterisk
        data={injectTypeOptions}
        value={serialKeyInjectType || null}
        onChange={(v) => setSerialKeyInjectType(v ?? "")}
        allowDeselect={false}
        disabled={loading}
      />
      <Select
        label="VAN 타입"
        mt="md"
        size="lg"
        withAsterisk
        placeholder="선택"
        data={vanTypeOptions}
        value={vanType || null}
        onChange={(v) => setVanType(v ?? "")}
        allowDeselect={false}
        disabled={loading}
      />
      <Select
        label="색상"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "BLACK", label: "BLACK" },
          { value: "WHITE", label: "WHITE" },
        ]}
        value={terminalColor || null}
        onChange={(v) => setTerminalColor(v ?? "WHITE")}
        allowDeselect={false}
        disabled={loading}
      />
      <Select
        label="DIK주입"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "N", label: "N (미주입)" },
          { value: "Y", label: "Y (주입)" },
        ]}
        value={dikYn}
        onChange={(v) => setDikYn(v ?? "N")}
        allowDeselect={false}
        disabled={loading}
      />
      <NumberInput
        label="시리얼케이블"
        mt="md"
        size="lg"
        withAsterisk
        min={0}
        value={serialCableCount}
        onChange={(v) => setSerialCableCount(v === "" ? "" : Number(v))}
        disabled={loading}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleRegister}
        disabled={!isValid || loading}
      >
        등록
      </Button>
    </Modal>
  );
}

export function TerminalModelUpdate({
  onConfirm,
  onCancel,
  row,
}: TerminalModelUpdateProps) {
  const [useYn, setUseYn] = useState(row.useYn || "Y");
  const [dikYn, setDikYn] = useState(row.dikYn || "N");
  const [serialCableCount, setSerialCableCount] = useState<number | "">(
    row.serialCableCount ?? 1,
  );
  const [loading, setLoading] = useState(false);

  const isChanged =
    useYn !== (row.useYn || "Y") ||
    dikYn !== (row.dikYn || "N") ||
    serialCableCount !== (row.serialCableCount ?? 1);

  const handleUpdate = async () => {
    if (useYn !== "Y" && useYn !== "N") {
      toast.error("사용 여부는 Y 또는 N만 선택할 수 있습니다.");
      return;
    }
    if (dikYn !== "Y" && dikYn !== "N") {
      toast.error("DIK주입은 Y 또는 N만 선택할 수 있습니다.");
      return;
    }
    if (serialCableCount === "" || Number(serialCableCount) < 0) {
      toast.error("시리얼케이블 개수는 0 이상이어야 합니다.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/terminal/model", {
        terminalModelId: row.terminalModelId,
        useYn,
        dikYn,
        serialCableCount: Number(serialCableCount),
      });
      if (res.data?.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "단말기 모델 수정 성공",
          content: {
            terminalModelId: row.terminalModelId,
            useYn,
            dikYn,
            serialCableCount,
          },
        });
        onConfirm?.(true);
      } else {
        toast.error(res.data?.msg || "수정에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "수정에 실패하였습니다.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title="단말기 모델 수정"
      closeOnClickOutside={false}
    >
      <Text size="sm" c="dimmed" mt="md">
        내부모델/외부모델과 주입 타입은 변경할 수 없습니다. DIK주입,
        시리얼케이블, 사용 여부를 수정할 수 있습니다.
      </Text>
      <TextInput
        label="내부모델"
        mt="md"
        size="lg"
        value={row.internalModelName}
        disabled
      />
      <TextInput
        label="외부모델"
        mt="md"
        size="lg"
        value={row.externalModelName}
        disabled
      />
      <Select
        label="DIK주입"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "N", label: "N (미주입)" },
          { value: "Y", label: "Y (주입)" },
        ]}
        value={dikYn}
        onChange={(v) => setDikYn(v ?? "N")}
        allowDeselect={false}
        disabled={loading}
      />
      <NumberInput
        label="시리얼케이블"
        mt="md"
        size="lg"
        withAsterisk
        min={0}
        value={serialCableCount}
        onChange={(v) => setSerialCableCount(v === "" ? "" : Number(v))}
        disabled={loading}
      />
      <Select
        label="사용 여부"
        mt="md"
        size="lg"
        withAsterisk
        data={[
          { value: "Y", label: "Y (사용)" },
          { value: "N", label: "N (미사용)" },
        ]}
        value={useYn}
        onChange={(v) => setUseYn(v ?? "Y")}
        allowDeselect={false}
        disabled={loading}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUpdate}
        disabled={!isChanged || loading}
      >
        수정
      </Button>
    </Modal>
  );
}

export function UserUpdate({
  onConfirm,
  onCancel,
  row,
  workerYn,
}: UserUpdateProps) {
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const [roleCodeOptions, setRoleCodeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [roleCode, setRoleCode] = useState("");
  const [userNameError, setUserNameError] = useState<string | null>(null);
  const userTypeName = workerYn === "Y" ? "작업자" : "유저";

  useEffect(() => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: workerYn,
        },
      })
      .then((res) => {
        const mapped = res.data.data.list.map((item: any) => ({
          value: item.roles,
          label: item.roleName,
        }));
        setRoleCodeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
        setRoleCodeOptions([]);
      });

    setUserName(row.userName);
    setRoleCode(row.roles || "");
  }, []);

  useEffect(() => {
    if (userName === row.userName && roleCode === row.roles) {
      setIsValidate(false);
    } else {
      setIsValidate(true);
    }
  }, [userName, roleCode]);

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserName(value);

    if (value.length < 2) {
      setUserNameError("이름은 최소 2글자 이상이여야합니다.");
    } else {
      setUserNameError(null);
    }
  };

  const handleUserUpdate = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/user", {
        userNo: row.userNo,
        userId: row.userId,
        userName: userName,
        roleCode: roleCode,
      });
      if (res.data.code === "200") {
        onConfirm?.(true);
        await setActionLog({
          eventType: "UPDATE",
          title: `${userTypeName}수정 성공`,
          content: JSON.stringify({
            userNo: row.userNo,
            userId: row.userId,
            userName,
            roleCode,
          }),
        });
      } else {
        toast.error(res.data.msg || `${userTypeName}수정 실패`);
        await setActionLog({
          eventType: "UPDATE",
          title: `${userTypeName}수정 실패`,
          content: JSON.stringify({
            userNo: row.userNo,
            userId: row.userId,
            userName,
            roleCode,
          }),
        });
      }
    } catch (err) {
      toast.error(`${userTypeName}수정 중 오류 발생`);
      await setActionLog({
        eventType: "UPDATE",
        title: `${userTypeName}수정 실패`,
        content: err,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={`${userTypeName} 수정`}
      closeOnClickOutside={false}
    >
      <TextInput
        label="아이디"
        mt="md"
        size="lg"
        placeholder="아이디"
        withAsterisk
        value={row.userId}
        disabled={true}
      />
      <TextInput
        label="이름"
        mt="md"
        size="lg"
        placeholder="이름"
        withAsterisk
        value={userName}
        onChange={handleUserName}
        disabled={loading}
        error={userNameError}
      />
      <Select
        label="권한"
        mt="md"
        size="lg"
        withAsterisk
        data={roleCodeOptions}
        defaultValue={roleCode}
        onChange={(_value, option) => setRoleCode(_value || "")}
        clearable={false}
        allowDeselect={false}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        leftSection={<IconUserCircle size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserUpdate}
        disabled={!isValidate}
      >
        수정
      </Button>
    </Modal>
  );
}

export function RoleAdd({ onConfirm, onCancel }: RoleAddProps) {
  type WorkerYn = "Y" | "N";
  const [resourceList, setResourceList] = useState<
    { resourcesPermissionId: string; url: string; urlName: string }[]
  >([]);
  const [roles, setRoles] = useState("");
  const [roleName, setRoleName] = useState("");
  const [workerYn, setWorkerYn] = useState<WorkerYn>("N");
  const [isLoading, setIsLoading] = useState(true);

  const [checkedStates, setCheckedStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});
  const [initialStates, setInitialStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});

  useEffect(() => {
    setting();
  }, []);

  const setting = async () => {
    try {
      const r_res = await axiosInstance.get("/api/v1/role/resource");
      const r = r_res.data.data;
      setResourceList(r);

      if (r.length > 0) {
        const init: { [id: number]: { read: boolean; write: boolean } } = {};
        r.forEach(
          (item: {
            resourcesPermissionId: string;
            url: string;
            urlName: string;
          }) => {
            init[Number(item.resourcesPermissionId)] = {
              read: false,
              write: false,
            };
          },
        );
        setCheckedStates(init);
        setInitialStates(init);
      }
    } catch (err) {
      setResourceList([]);
      setCheckedStates({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCheckedStates(JSON.parse(JSON.stringify(initialStates)));
    setRoles("");
    setRoleName("");
  };

  const handleUpdate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resParam = {
        roles: roles,
        rolesName: roleName,
        workerYn: workerYn,
        role_permissions: resourceList.map((res) => {
          const state = checkedStates[Number(res.resourcesPermissionId)] || {
            read: false,
            write: false,
          };
          return {
            resourcesPermissionId: Number(res.resourcesPermissionId),
            isRead: state.read ? "Y" : "N",
            isWrite: state.write ? "Y" : "N",
          };
        }),
      };
      const res = await axiosInstance.post("/api/v1/role/permission", resParam);
      if (res.data.code !== "200") throw new Error("권한등록 실패");
      await setActionLog({
        eventType: "CREATE",
        title: "권한등록 성공",
        content: resParam,
      });
      onConfirm?.(true);
    } catch (err: any) {
      await setActionLog({
        eventType: "CREATE",
        title: "권한등록 실패",
        content: err,
      });
      toast.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const rows = resourceList.map((row) => {
    const state = checkedStates[Number(row.resourcesPermissionId)] || {
      read: false,
      write: false,
    };

    const handleRowToggle = () => {
      const newValue = !(state.read && state.write);
      // ✅ read & write 둘 다 true면 → false로, 하나라도 false면 → 둘 다 true로

      setCheckedStates((prev) => ({
        ...prev,
        [Number(row.resourcesPermissionId)]: {
          read: newValue,
          write: newValue,
        },
      }));
    };

    return (
      <Table.Tr key={row.resourcesPermissionId}>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.urlName}
        </Table.Td>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.url}
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.read}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  read: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.write}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  write: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Modal size={"70%"} opened={true} onClose={onCancel} title={"권한 등록"}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Grid>
        <Grid.Col span={4}>
          <Flex justify={"space-between"} pb={"1rem"}>
            <Button
              variant="default"
              leftSection={<IconRefresh stroke={1.5} />}
              onClick={() => {
                handleReset();
              }}
              disabled={isLoading}
            >
              되돌리기
            </Button>
            <Button
              leftSection={<IconCheck stroke={1.5} />}
              {...primaryActionProps}
              onClick={() => {
                handleUpdate();
              }}
              disabled={isLoading}
            >
              등록
            </Button>
          </Flex>
          <TextInput
            pb={8}
            label="권한 ID"
            description="예시) ROLE_EXAMPLE"
            placeholder="권한 ID"
            withAsterisk
            value={roles}
            onChange={(e) => {
              setRoles(e.target.value);
            }}
          />
          <TextInput
            pb={8}
            label="권한 이름"
            description="예시) 테스트 사용자"
            placeholder="권한 이름"
            withAsterisk
            value={roleName}
            onChange={(e) => {
              setRoleName(e.target.value);
            }}
          />
          <Flex justify={"flex-end"} pb={8}>
            <Checkbox
              label="작업자 전용권한"
              checked={workerYn === "Y"}
              onChange={(e) => {
                setWorkerYn(e.target.checked ? "Y" : "N");
              }}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span="auto">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>메뉴</Table.Th>
                <Table.Th>URL</Table.Th>
                <Table.Th>읽기</Table.Th>
                <Table.Th>실행</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export function RoleUpdate({ onConfirm, onCancel, row }: RoleUpdateProps) {
  type WorkerYn = "Y" | "N";
  const [resourceList, setResourceList] = useState<
    { resourcesPermissionId: string; url: string; urlName: string }[]
  >([]);
  const [roles, setRoles] = useState("");
  const [roleName, setRoleName] = useState("");
  const [workerYn, setWorkerYn] = useState<WorkerYn>("N");
  const [roleInfo, setRoleInfo] = useState<RoleList | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [checkedStates, setCheckedStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});
  const [initialStates, setInitialStates] = useState<{
    [id: number]: { read: boolean; write: boolean };
  }>({});

  useEffect(() => {
    setting();
  }, []);

  const setting = async () => {
    console.log(row);
    try {
      const r_res = await axiosInstance.get("/api/v1/role/resource");
      const r = r_res.data.data;
      setResourceList(r);
      setRoleInfo(row);
      setRoles(row.roles);
      setRoleName(row.roleName);
      setWorkerYn(row.workerYn);

      if (r.length > 0) {
        const init: { [id: number]: { read: boolean; write: boolean } } = {};
        r.forEach(
          (item: {
            resourcesPermissionId: string;
            url: string;
            urlName: string;
          }) => {
            const perm = row.permissions.find(
              (pItem: RoleListPermissions) =>
                pItem.resourcesPermissionId ===
                Number(item.resourcesPermissionId),
            );
            init[Number(item.resourcesPermissionId)] = {
              read: perm?.isRead === "Y",
              write: perm?.isWrite === "Y",
            };
          },
        );
        setCheckedStates(init);
        setInitialStates(init);
      }
    } catch (err) {
      console.log(err);
      setResourceList([]);
      setRoleInfo(null);
      setCheckedStates({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCheckedStates(JSON.parse(JSON.stringify(initialStates)));
    setRoleName(roleInfo?.roleName || "");
    setWorkerYn(roleInfo?.workerYn || "N");
  };

  const handleUpdate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resParams = {
        roles: row.roles,
        rolesName: roleName,
        workerYn: workerYn,
        role_permissions: resourceList.map((res) => {
          const state = checkedStates[Number(res.resourcesPermissionId)] || {
            read: false,
            write: false,
          };
          return {
            resourcesPermissionId: Number(res.resourcesPermissionId),
            isRead: state.read ? "Y" : "N",
            isWrite: state.write ? "Y" : "N",
          };
        }),
      };
      const res = await axiosInstance.post(
        "/api/v1/role/permission",
        resParams,
      );
      if (res.data.code !== "200") throw new Error("권한수정 실패");
      await setActionLog({
        eventType: "UPDATE",
        title: "권한수정 성공",
        content: resParams,
      });
      onConfirm?.(true);
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "권한수정 실패",
        content: err,
      });
      toast.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const rows = resourceList.map((row) => {
    const state = checkedStates[Number(row.resourcesPermissionId)] || {
      read: false,
      write: false,
    };

    const handleRowToggle = () => {
      const newValue = !(state.read && state.write);
      // ✅ read & write 둘 다 true면 → false로, 하나라도 false면 → 둘 다 true로

      setCheckedStates((prev) => ({
        ...prev,
        [Number(row.resourcesPermissionId)]: {
          read: newValue,
          write: newValue,
        },
      }));
    };

    return (
      <Table.Tr key={row.resourcesPermissionId}>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.urlName}
        </Table.Td>
        <Table.Td
          style={{ color: "blue", cursor: "pointer" }} // 링크처럼 보이게
          onClick={handleRowToggle}
        >
          {row.url}
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.read}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  read: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
        <Table.Td>
          <Checkbox
            checked={state.write}
            onChange={(e) =>
              setCheckedStates((prev) => ({
                ...prev,
                [Number(row.resourcesPermissionId)]: {
                  ...state,
                  write: e.target.checked,
                },
              }))
            }
          />
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Modal size={"70%"} opened={true} onClose={onCancel} title={"권한 수정"}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Grid>
        <Grid.Col span={4}>
          <Flex justify={"space-between"} pt={"1rem"}>
            <Button
              variant="default"
              leftSection={<IconRefresh stroke={1.5} />}
              onClick={() => {
                handleReset();
              }}
              disabled={isLoading}
            >
              되돌리기
            </Button>
            <Button
              leftSection={<IconCheck stroke={1.5} />}
              {...primaryActionProps}
              onClick={() => {
                handleUpdate();
              }}
              disabled={isLoading}
            >
              수정
            </Button>
          </Flex>
          <TextInput
            pb={8}
            label="권한 ID"
            placeholder="ex) ROLE_EXAMPLE"
            value={roles}
            disabled={true}
          />
          <TextInput
            pb={8}
            label="권한 이름"
            description="예시) 테스트 사용자"
            placeholder="권한 이름"
            withAsterisk
            value={roleName}
            onChange={(e) => {
              setRoleName(e.target.value);
            }}
          />
          <Flex justify={"flex-end"} pb={8}>
            <Checkbox
              label="작업자 전용권한"
              checked={workerYn === "Y"}
              onChange={(e) => {
                setWorkerYn(e.target.checked ? "Y" : "N");
              }}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span="auto">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>메뉴</Table.Th>
                <Table.Th>URL</Table.Th>
                <Table.Th>읽기</Table.Th>
                <Table.Th>실행</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export function PasswordChange({ onConfirm, onCancel }: PasswordChangeProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [loading, setLoading] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const [password2Error, setPassword2Error] = useState<string | null>(null);

  useEffect(() => {
    if (oldPassword && password && password2 && password === password2) {
      setIsValidate(true);
    } else {
      setIsValidate(false);
    }
  }, [oldPassword, password, password2]);

  useEffect(() => {
    if (password === password2) {
      setPassword2Error(null);
    } else {
      setPassword2Error("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
    }
  }, [password2]);

  const handleUserRegist = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch("/api/v1/user/pw", {
        currentPassword: oldPassword,
        newPassword: password,
      });
      if (res.data.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "비밀번호 변경 성공",
        });
        onConfirm?.(true);
      } else {
        toast.error("비밀번호 변경 실패");
        await setActionLog({
          eventType: "UPDATE",
          title: "비밀번호 변경 실패",
        });
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "비밀번호 변경 실패",
        content: err,
      });
      toast.error("비밀번호 변경 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={"비밀번호 변경"}
      closeOnClickOutside={false}
    >
      <PasswordInput
        label="기존 비밀번호"
        mt="md"
        size="lg"
        placeholder="기존 비밀번호"
        withAsterisk
        value={oldPassword}
        onChange={(e) => {
          setOldPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="신규 비밀번호"
        mt="md"
        size="lg"
        placeholder="신규 비밀번호"
        withAsterisk
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
        disabled={loading}
      />
      <PasswordInput
        label="신규 비밀번호 확인"
        mt="md"
        size="lg"
        placeholder="신규 비밀번호 확인"
        withAsterisk
        value={password2}
        onChange={(e) => {
          setPassword2(e.target.value);
        }}
        disabled={loading}
        error={password2Error}
      />
      <Button
        fullWidth
        type="button"
        mt="lg"
        {...formPrimaryActionProps}
        leftSection={<IconLock size={24} stroke={1.5} />}
        loading={loading}
        loaderProps={{ type: "dots" }}
        onClick={handleUserRegist}
        disabled={!isValidate}
      >
        비밀번호 변경
      </Button>
    </Modal>
  );
}

interface TerminalKeyInjectionLog {
  terminalId: number;
  keyType: number;
  keyTypeName: string;
  userNo: number;
  userName: string;
  result: "SUCCESS" | "FAILURE" | string;
  message: string;
  createdDate: string;
}

export function TerminalView({ onConfirm, onCancel, row }: TerminalViewProps) {
  const [vanMap, setVanMap] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<TerminalKeyInjectionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);

  const injectLabel = (yn: string) => (yn === "Y" ? "주입" : "미주입");
  const dikInjectLabel = (item: {
    securityKeyRef1?: string | null;
    securityKeyRef2?: string | null;
    refInjectionYn?: string | null;
    dikYn?: string | null;
  }) => {
    const isDikModel = item.dikYn === "Y";
    const hasDik =
      Boolean((item.securityKeyRef1 ?? "").trim()) ||
      Boolean((item.securityKeyRef2 ?? "").trim());
    if (!isDikModel && !hasDik) return "없음";
    return injectLabel(item.refInjectionYn ?? "N");
  };

  /** 표시용: C4E7BE0B12BF → C4E7BE 0B12BF */
  const formatMacAddressDisplay = (mac: string | null | undefined) => {
    const value = (mac ?? "").trim();
    if (!value) return "-";
    const compact = value.replace(/[\s:\-]/g, "").toUpperCase();
    if (/^[0-9A-F]{12}$/.test(compact)) {
      return `${compact.slice(0, 6)} ${compact.slice(6)}`;
    }
    return value;
  };

  /** DIK 표시용: 앞 3자리 제외 후 5자리 추출 (예: 0007156523040400 → 71565) */
  const formatDikDisplay = (dik: string | null | undefined) => {
    const value = (dik ?? "").trim();
    if (!value) return "-";
    if (value.length >= 8) {
      return value.slice(3, 8);
    }
    if (value.length > 3) {
      return value.slice(3);
    }
    return value;
  };

  useEffect(() => {
    void getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    setLogsLoading(true);
    try {
      const [vanRes, logRes] = await Promise.allSettled([
        axiosInstance.get("/api/v1/common/van"),
        row?.terminalId
          ? axiosInstance.get(`/api/v1/terminal/${row.terminalId}/injection-logs`)
          : Promise.reject("No terminalId"),
      ]);
      if (vanRes.status === "fulfilled") {
        const vanData: Record<string, string> = {};
        (vanRes.value.data?.data ?? []).forEach(
          (item: { code: string; name: string }) => {
            vanData[item.code] = item.name;
          },
        );
        setVanMap(vanData);
      }
      if (logRes.status === "fulfilled") {
        setLogs(logRes.value.data?.data ?? []);
      }
    } catch {
      setVanMap({});
      setLogs([]);
    } finally {
      setLoading(false);
      setLogsLoading(false);
    }
  };

  const thWidth = 160;
  const info = (
    <Table
      variant="vertical"
      layout="fixed"
      withTableBorder
      withColumnBorders
      mb={"1rem"}
      fz={"1.125rem"}
    >
      <Table.Tbody>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            S/N
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.spSerialNo || "-"}</Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            MAC 주소
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {formatMacAddressDisplay(row.macAddress)}
          </Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            DIK
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {formatDikDisplay(row.securityKeyRef1)}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            내부모델
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.internalModelName || "-"}</Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            외부모델
          </Table.Th>
          <Table.Td bg={"#FFF"}>{row.externalModelName || "-"}</Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            VAN / 색상
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {vanMap[row.vanType] ?? row.vanType ?? "-"} / {row.terminalColor || "-"}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            S/N 주입
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {injectLabel(row.spSerialInjectionYn)}
          </Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            MAC 주입
          </Table.Th>
          <Table.Td bg={"#FFF"}>
            {injectLabel(row.macInjectionYn)}
          </Table.Td>
          <Table.Th w={thWidth} p={"0.25rem 1.25rem"} ta={"right"}>
            DIK 주입
          </Table.Th>
          <Table.Td bg={"#FFF"}>{dikInjectLabel(row)}</Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );

  return (
    <Modal opened={true} onClose={onCancel} title={"단말기 상세"} size={"100%"}>
      {loading ? (
        <Text p={12} c="dimmed">
          불러오는 중...
        </Text>
      ) : (
        <>
          {info}
          <Box mt={"1.25rem"}>
            <Text fw={700} fz={"1.125rem"} mb={"0.5rem"}>
              키 주입 이력
            </Text>
            <ScrollArea.Autosize
              mah={320}
              bg={"#fff"}
              style={{ border: "1px solid #dee2e6", borderRadius: 4 }}
            >
              <Table
                layout="fixed"
                withTableBorder={false}
                withColumnBorders
                striped
                highlightOnHover
              >
                <Table.Thead
                  bg={"#f5f6fa"}
                  pos={"sticky"}
                  top={0}
                  style={{ zIndex: 1 }}
                >
                  <Table.Tr>
                    <Table.Th w={60} ta="center" p={"8px 12px"}>
                      번호
                    </Table.Th>
                    <Table.Th w={120} ta="center" p={"8px 12px"}>
                      구분
                    </Table.Th>
                    <Table.Th w={90} ta="center" p={"8px 12px"}>
                      결과
                    </Table.Th>
                    <Table.Th ta="left" p={"8px 12px"}>
                      상세 내용
                    </Table.Th>
                    <Table.Th w={120} ta="center" p={"8px 12px"}>
                      작업자
                    </Table.Th>
                    <Table.Th w={180} ta="center" p={"8px 12px"}>
                      작업일시
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {logs.length > 0 ? (
                    logs.map((log, idx) => {
                      const isSuccess = log.result === "SUCCESS";
                      return (
                        <Table.Tr key={idx}>
                          <Table.Td ta="center" p={"6px 12px"}>
                            {logs.length - idx}
                          </Table.Td>
                          <Table.Td ta="center" p={"6px 12px"}>
                            <Badge
                              variant="light"
                              color={
                                log.keyType === 30
                                  ? "blue"
                                  : log.keyType === 40
                                  ? "grape"
                                  : log.keyType === 20
                                  ? "orange"
                                  : "gray"
                              }
                              size="sm"
                            >
                              {log.keyTypeName ||
                                (log.keyType === 30
                                  ? "SP S/N"
                                  : log.keyType === 40
                                  ? "MAC"
                                  : log.keyType === 20
                                  ? "DIK"
                                  : "기타")}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="center" p={"6px 12px"}>
                            <Badge
                              variant="filled"
                              color={isSuccess ? "teal" : "red"}
                              size="sm"
                            >
                              {isSuccess ? "성공" : "실패"}
                            </Badge>
                          </Table.Td>
                          <Table.Td p={"6px 12px"} fw={500}>
                            {log.message || "-"}
                          </Table.Td>
                          <Table.Td ta="center" p={"6px 12px"}>
                            {log.userName || "-"}
                          </Table.Td>
                          <Table.Td ta="center" p={"6px 12px"}>
                            {log.createdDate || "-"}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={6} ta="center" p={"1.5rem"} c="dimmed">
                        {logsLoading
                          ? "불러오는 중..."
                          : "키 주입 이력이 없습니다."}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </Box>
        </>
      )}
      <Flex justify={"flex-end"} mt={"1rem"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          variant="default"
        >
          닫기
        </Button>
      </Flex>
    </Modal>
  );
}

export function ActionLogDetail({
  onConfirm,
  onCancel,
  row,
}: ActionLogDetailProps) {
  return (
    <Modal opened={true} onClose={onCancel} title={"로그 상세"} size={"lg"}>
      <ScrollArea h={"60vh"} pos={"relative"} bg={"#fff"} bdrs={8}>
        <Table>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} w={120} pos={"sticky"} top={0}>
                항목
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                내용
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                일시
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                {boardDateFormat(row.createdDate)}
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                분류
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.eventType}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                메뉴
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.menuUrl}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                사용자
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.userName}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                안드로이드 S/N
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.serialNo || "-"}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                키워드
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.keyword || "-"}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                행동
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.action}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td p={"8px 12px"} fw={600} bg={"#f8f9fa"}>
                IP
              </Table.Td>
              <Table.Td p={"8px 12px"}>{row.ip}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td
                p={"8px 12px"}
                fw={600}
                bg={"#f8f9fa"}
                style={{ verticalAlign: "top" }}
              >
                User Agent
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                <Text
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "0.8rem",
                    lineHeight: "1.3",
                  }}
                >
                  {row.userAgent}
                </Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td
                p={"8px 12px"}
                fw={600}
                bg={"#f8f9fa"}
                style={{ verticalAlign: "top" }}
              >
                내용
              </Table.Td>
              <Table.Td p={"8px 12px"}>
                <Text
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                    lineHeight: "1.4",
                  }}
                >
                  {row.message}
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Flex justify={"flex-end"} mt={"1rem"}>
        <Button
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() => {
            onConfirm?.(false);
          }}
          variant="default"
        >
          닫기
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalDelete({ onConfirm, onCancel }: TerminalDeleteProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any[]>([]);
  const [canDelete, setCanDelete] = useState(false);
  const theme = useMantineTheme();

  const handleCheck = async () => {
    if (!startId || !endId) {
      setError("번호를 입력해주세요.");
      return;
    }

    setError(null);
    setInfoMessage(null);
    setChecking(true);

    try {
      // 삭제 가능 여부 확인 API 호출 (GET, params)
      const response = await axiosInstance.get(
        "/api/v1/terminal/delete/check",
        {
          params: {
            startAndroidSn: startId,
            endAndroidSn: endId,
          },
        },
      );

      if (response.data.code === "200") {
        const data = response.data.data ?? {};
        const terminals = Array.isArray(data.terminals) ? data.terminals : [];
        const totalCount = Number(data.totalCount ?? terminals.length ?? 0);
        const deletable = data.deletable === true;
        setCheckResult(terminals);
        setInfoMessage(data.message || null);
        setCanDelete(totalCount > 0 && deletable);
        if (totalCount === 0) {
          setError("삭제할 단말기가 없습니다.");
        } else if (!deletable) {
          setError(
            data.message ||
            "주입된 단말기가 있어 삭제 할 수 없습니다. 관리자에게 문의하십시요",
          );
          setInfoMessage(null);
        }
      } else {
        setError(response.data.msg || "확인 중 오류가 발생했습니다.");
        setCanDelete(false);
        setCheckResult([]);
        setInfoMessage(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.msg || "확인 중 오류가 발생했습니다.");
      setCanDelete(false);
      setCheckResult([]);
      setInfoMessage(null);
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError("먼저 확인 버튼을 눌러주세요.");
      return;
    }

    const confirmMessage =
      infoMessage || "삭제할 단말기가 있습니다. 정말 삭제하시겠습니까?";
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get("/api/v1/terminal/delete", {
        params: {
          startAndroidSn: startId,
          endAndroidSn: endId,
        },
      });

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "단말기 일괄 삭제 성공",
          content: `S/N: ${startId}~${endId}`,
        });
        if ((response.data.data?.deletedCount ?? 0) === 0) {
          toast.info("삭제할 단말기가 없습니다.");
        } else {
          toast.success("삭제가 완료되었습니다.");
        }
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "삭제 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "DELETE",
        title: "단말기 일괄 삭제 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "삭제 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 삭제" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        삭제할 단말기의 S/N 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end">
        <TextInput
          label="시작 S/N"
          placeholder="시작 S/N"
          value={startId}
          onChange={(e) => {
            setStartId(e.target.value.toUpperCase());
            setCanDelete(false);
            setInfoMessage(null);
            setError(null);
          }}
          style={{ flex: 1 }}
        />
        <Text style={{ paddingBottom: "0.5rem" }}>~</Text>
        <TextInput
          label="끝 S/N"
          placeholder="끝 S/N"
          value={endId}
          onChange={(e) => {
            setEndId(e.target.value.toUpperCase());
            setCanDelete(false);
            setInfoMessage(null);
            setError(null);
          }}
          style={{ flex: 1 }}
        />
        <Button
          onClick={handleCheck}
          disabled={checking || loading}
          loading={checking}
          {...searchActionProps}
        >
          {labels.confirm}
        </Button>
      </Flex>

      {error && (
        <Text size="sm" c="red" mt="xs" fw={600}>
          {error}
        </Text>
      )}

      {!error && infoMessage && (
        <Text size="sm" c={canDelete ? "blue" : "dimmed"} mt="xs">
          {infoMessage}
        </Text>
      )}

      {checkResult.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <Text size="sm" fw={600} mb="xs">
            확인 결과 ({checkResult.length}개)
          </Text>
          <ScrollArea h={200}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>번호</Table.Th>
                  <Table.Th>S/N</Table.Th>
                  <Table.Th>S/N 주입</Table.Th>
                  <Table.Th>MAC 주입</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {checkResult.map((item) => {
                  const hasInjected =
                    item.spSerialInjectionYn === "Y" ||
                    item.macInjectionYn === "Y";
                  return (
                    <Table.Tr
                      key={item.terminalId}
                      bg={hasInjected ? "#ffe6e6" : undefined}
                    >
                      <Table.Td>{item.terminalId}</Table.Td>
                      <Table.Td>{item.spSerialNo || "-"}</Table.Td>
                      <Table.Td>
                        {item.spSerialInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                      <Table.Td>
                        {item.macInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </div>
      )}

      <Flex justify="space-between" mt="lg">
        <Button
          onClick={onCancel}
          leftSection={<IconX size={18} />}
          {...cancelActionProps}
        >
          {labels.cancel}
        </Button>
        <Button
          onClick={handleDelete}
          loading={loading}
          disabled={!canDelete}
          leftSection={<IconX size={18} />}
          {...dangerActionProps}
        >
          {labels.delete}
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalIncoming({
  onConfirm,
  onCancel,
}: TerminalIncomingProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any[]>([]);
  const [canDelete, setCanDelete] = useState(false);
  const theme = useMantineTheme();

  const todayStr = dayjs().format("YYYY-MM-DD"); // KST 기준 오늘 날짜
  const lastMonthFirstDay = dayjs()
    .subtract(1, "month") // 저번 달로 이동
    .startOf("month") // 그 달의 첫째 날
    .format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const handleCheck = async () => {
    if (!startId || !endId) {
      setError("S/N 혹은 날짜를 입력해주세요.");
      return;
    }

    setError(null);
    setChecking(true);

    try {
      // 출고 가능 여부 확인 API 호출 (GET, params)
      const response = await axiosInstance.get(
        "/api/v1/terminal/incoming/check",
        {
          params: {
            startSerialNo: startId,
            endSerialNo: endId,
            startDate: startDate,
            endDate: endDate,
          },
        },
      );

      if (response.data.code === "200") {
        setCheckResult(response.data.data);
        setCanDelete(true);
      } else {
        setError(response.data.msg || "확인 중 오류가 발생했습니다.");
        setCanDelete(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.msg || "확인 중 오류가 발생했습니다.");
      setCanDelete(false);
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError("먼저 확인 버튼을 눌러주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const incomingRequest = {
        startSerialNo: startId,
        endSerialNo: endId,
        startDate: startDate,
        endDate: endDate,
      };

      const response = await axiosInstance.post(
        "/api/v1/terminal/incoming",
        incomingRequest,
      );

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "DELETE",
          title: "단말기 일괄 출고 성공",
          content: `Android S/N: ${startId}~${endId}`,
        });
        toast.success("출고가 완료되었습니다.");
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "출고 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "DELETE",
        title: "단말기 일괄 출고 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "출고 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 출고" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        삭제할 단말기의 안드로이드 S/N 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end" mb="md">
        <TextInput
          label="시작 번호"
          placeholder="시작 번호"
          value={startId}
          onChange={(e) => {
            setStartId(e.target.value);
            setCanDelete(false);
          }}
          style={{ flex: 1 }}
        />
        {/* <Text style={{ paddingBottom: "0.5rem" }}>~</Text> */}
        <TextInput
          label="끝 번호"
          placeholder="끝 번호"
          value={endId}
          onChange={(e) => {
            setEndId(e.target.value);
            setCanDelete(false);
          }}
          style={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="sm" align="flex-end" mb="md">
        <DateInput
          label="시작 날짜"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={startDate}
          onChange={(val) => {
            setStartDate(val || undefined);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
          style={{ flex: 1 }}
        />
        <DateInput
          label="종료 날짜"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={endDate}
          onChange={(val) => {
            let newEnd = val || todayStr;
            if (startDate && new Date(newEnd) < new Date(startDate)) {
              newEnd = startDate;
            }
            setEndDate(newEnd);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
          style={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="sm" align="flex-end" mb="md">
        <Button
          onClick={handleCheck}
          disabled={checking || loading}
          loading={checking}
          {...searchActionProps}
        >
          {labels.confirm}
        </Button>
      </Flex>

      {error && (
        <Text size="sm" c="red" mt="xs">
          {error}
        </Text>
      )}

      {checkResult.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <Text size="sm" fw={600} mb="xs">
            확인 결과 ({checkResult.length}개)
          </Text>
          <ScrollArea h={200}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>번호</Table.Th>
                  <Table.Th>안드로이드 S/N</Table.Th>
                  <Table.Th>N사마스터키</Table.Th>
                  <Table.Th>S/N</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {checkResult.map((item) => {
                  const hasInjected =
                    item.keyInjectionYn === "Y" ||
                    item.spSerialInjectionYn === "Y";
                  return (
                    <Table.Tr
                      key={item.terminalId}
                      bg={hasInjected ? "#ffe6e6" : undefined}
                    >
                      <Table.Td>{item.terminalId}</Table.Td>
                      <Table.Td>{item.serialNo}</Table.Td>
                      <Table.Td>
                        {item.keyInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                      <Table.Td>
                        {item.spSerialInjectionYn === "Y" ? "주입" : "미주입"}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </div>
      )}

      <Flex justify="space-between" mt="lg">
        <Button
          variant="default"
          onClick={onCancel}
          leftSection={<IconX size={18} />}
        >
          취소
        </Button>
        <Button
          onClick={handleDelete}
          loading={loading}
          disabled={!canDelete}
          leftSection={<IconPackage size={18} />}
          color="green"
        >
          출고
        </Button>
      </Flex>
    </Modal>
  );
}

export function TerminalUpdate({ onConfirm, onCancel }: TerminalUpdateProps) {
  const [startId, setStartId] = useState("");
  const [endId, setEndId] = useState("");
  const [updateVan, setUpdateVan] = useState(false);
  const [updateModel, setUpdateModel] = useState(false);
  const [updateColor, setUpdateColor] = useState(false);
  const [vanType, setVanType] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const theme = useMantineTheme();

  useEffect(() => {
    // VAN 옵션 로드
    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setVanTypeOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
      });

    // 모델 옵션 로드
    axiosInstance
      .get("/api/v1/common/device")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setModelOptions(mapped);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const handleUpdate = async () => {
    const start = parseInt(startId);
    const end = parseInt(endId);

    if (!start || !end) {
      setError("번호를 입력해주세요.");
      return;
    }

    if (start > end) {
      setError("시작 번호가 끝 번호보다 큽니다.");
      return;
    }

    if (!updateVan && !updateModel && !updateColor) {
      setError("수정할 항목을 하나 이상 선택해주세요.");
      return;
    }

    if (updateVan && !vanType) {
      setError("VAN를 선택해주세요.");
      return;
    }

    if (updateModel && !model) {
      setError("모델을 선택해주세요.");
      return;
    }

    if (updateColor && !color) {
      setError("색상을 선택해주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateData: any = {
        startId: start,
        endId: end,
      };

      if (updateVan) {
        updateData.vanType = vanType;
      }
      if (updateModel) {
        updateData.deviceType = model;
      }
      if (updateColor) {
        updateData.deviceColor = color;
      }

      const response = await axiosInstance.patch(
        "/api/v1/terminal/bulk-update",
        updateData,
      );

      if (response.data.code === "200") {
        await setActionLog({
          eventType: "UPDATE",
          title: "단말기 일괄 수정 성공",
          content: JSON.stringify(updateData),
        });
        toast.success("수정이 완료되었습니다.");
        onConfirm?.(true);
      } else {
        setError(response.data.msg || "수정 중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "UPDATE",
        title: "단말기 일괄 수정 실패",
        content: err,
      });
      setError(err.response?.data?.msg || "수정 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onCancel} title="단말기 수정" size="lg">
      <Text size="sm" mb="md" c="dimmed">
        수정할 단말기의 번호 범위를 입력하세요.
      </Text>

      <Flex gap="sm" align="flex-end" mb="md">
        <TextInput
          label="시작 번호"
          placeholder="시작 번호"
          value={startId}
          onChange={(e) => setStartId(e.target.value)}
          style={{ flex: 1 }}
        />
        <Text style={{ paddingBottom: "0.5rem" }}>~</Text>
        <TextInput
          label="끝 번호"
          placeholder="끝 번호"
          value={endId}
          onChange={(e) => setEndId(e.target.value)}
          style={{ flex: 1 }}
        />
      </Flex>

      <Text size="sm" fw={600} mb="xs">
        수정할 항목 선택
      </Text>

      <Checkbox
        label="VAN 변경"
        checked={updateVan}
        onChange={(e) => setUpdateVan(e.currentTarget.checked)}
        mb="sm"
      />

      {updateVan && (
        <Select
          label="VAN"
          placeholder="VAN를 선택하세요"
          data={vanTypeOptions}
          value={vanType}
          onChange={(value) => setVanType(value || "")}
          mb="md"
        />
      )}

      <Checkbox
        label="모델 변경"
        checked={updateModel}
        onChange={(e) => setUpdateModel(e.currentTarget.checked)}
        mb="sm"
      />

      {updateModel && (
        <Select
          label="모델"
          placeholder="모델을 선택하세요"
          data={modelOptions}
          value={model}
          onChange={(value) => setModel(value || "")}
          mb="md"
        />
      )}

      <Checkbox
        label="색상 변경"
        checked={updateColor}
        onChange={(e) => setUpdateColor(e.currentTarget.checked)}
        mb="sm"
      />

      {updateColor && (
        <Select
          label="색상"
          placeholder="색상을 선택하세요"
          data={[
            { value: "BLACK", label: "BLACK" },
            { value: "WHITE", label: "WHITE" },
          ]}
          value={color}
          onChange={(value) => setColor(value || "")}
          mb="md"
        />
      )}

      {error && (
        <Text size="sm" c="red" mb="md">
          {error}
        </Text>
      )}

      <Flex justify="space-between">
        <Button
          onClick={onCancel}
          leftSection={<IconX size={18} />}
          {...cancelActionProps}
        >
          {labels.cancel}
        </Button>
        <Button
          onClick={handleUpdate}
          loading={loading}
          leftSection={<IconCheck size={18} />}
          {...secondaryActionProps}
        >
          {labels.edit}
        </Button>
      </Flex>
    </Modal>
  );
}

const DEFAULT_LOG_CONFIGS = [
  { eventType: "PAGE_VIEW", label: "페이지 조회", isEnabled: "Y" },
  { eventType: "BUTTON_CLICK", label: "버튼 클릭", isEnabled: "Y" },
  { eventType: "FORM_SUBMIT", label: "폼 제출", isEnabled: "Y" },
  { eventType: "LOGIN", label: "로그인", isEnabled: "Y" },
  { eventType: "LOGOUT", label: "로그아웃", isEnabled: "Y" },
  { eventType: "ACCESS", label: "접근", isEnabled: "Y" },
  { eventType: "ACCESS_DENIED", label: "접근 거부", isEnabled: "Y" },
  { eventType: "CREATE", label: "생성", isEnabled: "Y" },
  { eventType: "UPDATE", label: "수정", isEnabled: "Y" },
  { eventType: "DELETE", label: "삭제", isEnabled: "Y" },
  { eventType: "IMPORT", label: "가져오기", isEnabled: "Y" },
  { eventType: "EXPORT", label: "내보내기", isEnabled: "Y" },
  { eventType: "CONNECT", label: "연결", isEnabled: "Y" },
  { eventType: "INPUT", label: "입력", isEnabled: "Y" },
  { eventType: "KEY_INJECT", label: "키 주입", isEnabled: "Y" },
  { eventType: "API_CALL", label: "API 호출", isEnabled: "Y" },
];

const isDev = process.env.REACT_APP_IS_DEV === "true";

export function ActionLogConfigModal({
  onConfirm,
  onCancel,
}: ActionLogConfigModalProps) {
  const [configs, setConfigs] = useState<
    { eventType: string; label: string; isEnabled: string }[]
  >(DEFAULT_LOG_CONFIGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    if (isDev) {
      const saved = localStorage.getItem("action_log_configs");
      if (saved) {
        try {
          setConfigs(JSON.parse(saved));
          setLoading(false);
          return;
        } catch {}
      }
    }
    try {
      const res = await axiosInstance.get("/api/v1/logs/config");
      if (
        res.data?.data &&
        Array.isArray(res.data.data) &&
        res.data.data.length > 0
      ) {
        setConfigs(res.data.data);
      } else {
        const saved = localStorage.getItem("action_log_configs");
        if (saved) {
          try {
            setConfigs(JSON.parse(saved));
          } catch {
            setConfigs(DEFAULT_LOG_CONFIGS);
          }
        } else {
          setConfigs(DEFAULT_LOG_CONFIGS);
        }
      }
    } catch (err) {
      const saved = localStorage.getItem("action_log_configs");
      if (saved) {
        try {
          setConfigs(JSON.parse(saved));
        } catch {
          setConfigs(DEFAULT_LOG_CONFIGS);
        }
      } else {
        setConfigs(DEFAULT_LOG_CONFIGS);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (eventType: string) => {
    setConfigs((prev) =>
      prev.map((c) =>
        c.eventType === eventType
          ? { ...c, isEnabled: c.isEnabled === "Y" ? "N" : "Y" }
          : c
      )
    );
  };

  const handleSelectAll = (enabled: boolean) => {
    setConfigs((prev) =>
      prev.map((c) => ({
        ...c,
        isEnabled: enabled ? "Y" : "N",
      }))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      localStorage.setItem("action_log_configs", JSON.stringify(configs));
      if (!isDev) {
        await axiosInstance.put("/api/v1/logs/config", {
          configs,
        });
      }
      toast.success("로그 적재 설정이 저장되었습니다.");
      onConfirm(true);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "로그 적재 설정 저장에 실패했습니다. (백엔드 서버 재시작 확인 필요)"
      );
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = configs.filter((c) => c.isEnabled === "Y").length;
  const totalCount = configs.length;

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={
        <Group gap="xs">
          <IconSettings size={22} color="#1976d2" />
          <Text fw={700} fz="lg">
            로그 적재 설정
          </Text>
        </Group>
      }
      size="lg"
      centered
    >
      <LoadingOverlay visible={loading} />
      <Box mb="md">
        <Text size="sm" c="dimmed" mb="xs">
          시스템에 적재(수집)할 로그 분류를 선택하세요. 체크 해제된 분류는
          데이터베이스에 적재되지 않습니다.
        </Text>
        <Flex
          justify="space-between"
          align="center"
          bg="#f8f9fa"
          p="xs"
          style={{ borderRadius: 6 }}
        >
          <Text size="sm" fw={600}>
            적재 대상:{" "}
            <Text span c="blue" fw={700}>
              {enabledCount}
            </Text>{" "}
            / {totalCount}개
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="subtle"
              onClick={() => handleSelectAll(true)}
            >
              전체 선택
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => handleSelectAll(false)}
            >
              전체 해제
            </Button>
          </Group>
        </Flex>
      </Box>

      <Grid gutter="xs" mb="lg">
        {configs.map((cfg) => {
          const isChecked = cfg.isEnabled === "Y";
          return (
            <Grid.Col span={6} key={cfg.eventType}>
              <Box
                p="xs"
                style={{
                  border: isChecked
                    ? "1px solid #1976d2"
                    : "1px solid #dee2e6",
                  backgroundColor: isChecked ? "#f0f7ff" : "#fff",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onClick={() => handleToggle(cfg.eventType)}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={() => handleToggle(cfg.eventType)}
                  label={
                    <Box>
                      <Text size="sm" fw={600}>
                        {cfg.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {cfg.eventType}
                      </Text>
                    </Box>
                  }
                  styles={{
                    label: { cursor: "pointer", width: "100%" },
                    inner: { cursor: "pointer" },
                  }}
                />
              </Box>
            </Grid.Col>
          );
        })}
      </Grid>

      <Flex justify="space-between" mt="md">
        <Button
          onClick={onCancel}
          leftSection={<IconX size={18} />}
          {...cancelActionProps}
        >
          {labels.cancel}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          leftSection={<IconCheck size={18} />}
          {...primaryActionProps}
        >
          저장
        </Button>
      </Flex>
    </Modal>
  );
}

export interface ErrorCodeItem {
  code: string;
  category: string;
  exceptionType: string;
  name: string;
  description: string;
  actionGuide: string;
}

export const ERROR_CODE_LIST: ErrorCodeItem[] = [
  {
    code: "ERR-4510",
    category: "데이터베이스",
    exceptionType: "SQLSyntaxErrorException, BadSqlGrammarException",
    name: "DB 쿼리 문법/구문 오류",
    description: "MyBatis XML 매퍼 또는 SQL 쿼리 문법(키워드, 따옴표 누락, 컬럼명 오류 등)이 올바르지 않은 경우 발생합니다.",
    actionGuide: "웹 로그 상세의 시스템 에러 원인 확인 후 MyBatis XML 매퍼 SQL 구문(따옴표, 문법 등) 수정 필요",
  },
  {
    code: "ERR-4500",
    category: "데이터베이스",
    exceptionType: "SQLIntegrityConstraintViolationException, DuplicateKeyException",
    name: "DB 제약조건 위반 / 중복 데이터",
    description: "이미 존재하는 고유키(S/N, MAC 등)를 중복 등록하려 하거나 외래키 제약조건을 위반한 경우 발생합니다.",
    actionGuide: "기존에 이미 등록된 S/N 또는 MAC인지 확인하고, 중복되지 않는 고유 번호로 재시도",
  },
  {
    code: "ERR-4520",
    category: "데이터베이스",
    exceptionType: "CannotGetJdbcConnectionException, SQLTransientConnectionException",
    name: "DB 접속 및 연결 실패",
    description: "MariaDB 데이터베이스 서버에 연결할 수 없거나 커넥션 풀(HikariCP)이 고갈된 경우 발생합니다.",
    actionGuide: "DB 서버 기동 상태 및 네트워크 연결, DB 접속 계정/비밀번호 환경설정 점검",
  },
  {
    code: "ERR-4530",
    category: "데이터베이스",
    exceptionType: "QueryTimeoutException, SQLTimeoutException",
    name: "DB 쿼리 타임아웃",
    description: "대량 데이터 처리 시 쿼리 수행 시간이 제한 시간을 초과한 경우 발생합니다.",
    actionGuide: "대량 데이터 등록 시 배치 크기 분할 및 DB 인덱스/슬로우 쿼리 상태 점검",
  },
  {
    code: "ERR-4590",
    category: "데이터베이스",
    exceptionType: "PersistenceException, DataAccessException",
    name: "일반 데이터베이스 처리 오류",
    description: "데이터베이스 트랜잭션 또는 영속성 계층에서 처리 중 예외가 발생한 경우입니다.",
    actionGuide: "로그 상세 메시지 확인 후 해당 트랜잭션 로직 및 쿼리 파라미터 점검",
  },
  {
    code: "ERR-4501",
    category: "포맷 / 파싱",
    exceptionType: "JsonProcessingException",
    name: "요청 데이터(JSON) 파싱 오류",
    description: "클라이언트에서 전송한 JSON 문자열의 형식이 올바르지 않거나 DTO 필드 매핑이 실패한 경우입니다.",
    actionGuide: "요청 파라미터의 JSON 규격 및 필수 필드 값 누락 여부 확인",
  },
  {
    code: "ERR-4502",
    category: "파일 / 입출력",
    exceptionType: "IOException",
    name: "파일 입출력 처리 오류",
    description: "업로드된 파일 스트림을 읽거나 임시 파일을 저장하는 과정에서 I/O 오류가 발생한 경우입니다.",
    actionGuide: "파일 손상 여부 확인 및 정상 파일로 재업로드",
  },
  {
    code: "ERR-4000",
    category: "파일 / 업로드",
    exceptionType: "DataCountMismatchException",
    name: "데이터 건수 불일치",
    description: "엑셀 파일의 행 수와 첨부된 DIK ZIP 파일 내 키 데이터 건수가 서로 일치하지 않는 경우입니다.",
    actionGuide: "엑셀 단말기 목록 수량과 DIK 파일 수량을 동일하게 맞춰 재업로드",
  },
  {
    code: "ERR-4100",
    category: "파일 / 업로드",
    exceptionType: "FileIsEmptyException",
    name: "필수 파일 누락",
    description: "엑셀 파일 또는 필수 DIK ZIP 파일이 비어있거나 첨부되지 않은 경우입니다.",
    actionGuide: "엑셀 파일 및 DIK ZIP 파일이 정상적으로 선택되었는지 확인",
  },
  {
    code: "ERR-4001",
    category: "검증 / 파라미터",
    exceptionType: "IllegalArgumentException",
    name: "파라미터 유효성 검증 실패",
    description: "필수 입력값 누락, S/N 범위 초과, 수량 0 이하 등 입력 규칙에 맞지 않는 경우입니다.",
    actionGuide: "입력값 및 범위(시작번호, 끝번호, 생산대수)를 확인 후 재시도",
  },
  {
    code: "ERR-4003",
    category: "작업 제어",
    exceptionType: "JobCancelledException",
    name: "작업 취소",
    description: "비동기 작업 진행 도중 사용자가 수동으로 작업을 취소한 경우입니다.",
    actionGuide: "필요 시 신규 작업으로 재등록 진행",
  },
  {
    code: "ERR-4300~4305",
    category: "단말기 정책",
    exceptionType: "InvalidVanType, UnregisteredTerminalModel, InactiveTerminalModel 등",
    name: "단말기/VAN 모델 정책 불일치",
    description: "미등록 단말기 모델이거나 미사용(N) 처리된 모델, 지원하지 않는 VAN 코드인 경우입니다.",
    actionGuide: "단말기 모델 관리 메뉴에서 해당 모델의 등록 상태 및 사용 여부(Y) 확인",
  },
  {
    code: "ERR-4400",
    category: "비즈니스 상태",
    exceptionType: "IllegalStateException",
    name: "작업 진행 불가 상태",
    description: "키주입 미완료 등 선행 조건이 충족되지 않은 상태에서 후속 작업을 시도한 경우입니다.",
    actionGuide: "선행 조건(키주입 완료 여부 등)을 확인 후 다시 시도",
  },
  {
    code: "ERR-9999",
    category: "시스템",
    exceptionType: "UnknownException, RuntimeException",
    name: "서버 내부 미분류 오류",
    description: "예측하지 못한 서버 내부 런타임 오류가 발생한 경우입니다.",
    actionGuide: "웹 로그 관리 상세 모달에서 시스템 에러 스택 확인 후 관리자/개발자 문의",
  },
];

const getCategoryBadgeColor = (category: string): string => {
  switch (category) {
    case "데이터베이스":
      return "red";
    case "포맷 / 파싱":
      return "orange";
    case "파일 / 입출력":
    case "파일 / 업로드":
      return "violet";
    case "검증 / 파라미터":
      return "yellow";
    case "작업 제어":
      return "gray";
    case "단말기 정책":
      return "teal";
    case "비즈니스 상태":
      return "cyan";
    default:
      return "dark";
  }
};

export function ErrorCodeGuideModal({
  onConfirm,
  onCancel,
}: ErrorCodeGuideModalProps) {
  const [keyword, setKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("전체");

  const categories = ["전체", "데이터베이스", "파일 / 업로드", "포맷 / 파싱", "파일 / 입출력", "검증 / 파라미터", "단말기 정책", "시스템"];

  const filteredList = ERROR_CODE_LIST.filter((item) => {
    const matchesCategory =
      selectedCategory === "전체" || item.category === selectedCategory;
    const lowerKey = keyword.trim().toLowerCase();
    const matchesKeyword =
      !lowerKey ||
      item.code.toLowerCase().includes(lowerKey) ||
      item.name.toLowerCase().includes(lowerKey) ||
      item.exceptionType.toLowerCase().includes(lowerKey) ||
      item.description.toLowerCase().includes(lowerKey);
    return matchesCategory && matchesKeyword;
  });

  return (
    <Modal
      opened={true}
      onClose={onCancel}
      title={
        <Flex align="center" gap="xs">
          <IconInfoCircle size={22} color="#1c7ed6" />
          <Text fw={700} fz="1.15rem">
            표준 에러 코드 정의 가이드
          </Text>
        </Flex>
      }
      size="80%"
    >
      <Box mb="md">
        <Text size="sm" c="dimmed" mb="xs">
          S-MES 시스템 전반에서 발생하는 표준 에러 코드, 예외 클래스 유형, 상세 설명 및 조치 가이드입니다.
        </Text>
        <Flex gap="sm" align="center" wrap="wrap">
          <TextInput
            placeholder="에러 코드, 예외명, 설명 검색"
            leftSection={<IconSearch size={16} />}
            value={keyword}
            onChange={(e) => setKeyword(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 260 }}
            size="xs"
          />
          <Flex gap={4} wrap="wrap">
            {categories.map((cat) => (
              <Badge
                key={cat}
                variant={selectedCategory === cat ? "filled" : "light"}
                color={cat === "전체" ? "blue" : getCategoryBadgeColor(cat)}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </Badge>
            ))}
          </Flex>
        </Flex>
      </Box>

      <ScrollArea h="60vh" offsetScrollbars>
        <Table striped highlightOnHover withTableBorder withColumnBorders style={{ fontSize: "0.85rem" }}>
          <Table.Thead bg="#f8f9fa">
            <Table.Tr>
              <Table.Th style={{ width: 110, textAlign: "center" }}>에러 코드</Table.Th>
              <Table.Th style={{ width: 100, textAlign: "center" }}>분류</Table.Th>
              <Table.Th style={{ width: 160 }}>에러 명칭</Table.Th>
              <Table.Th style={{ width: 220 }}>발생 예외 유형</Table.Th>
              <Table.Th>설명 및 발생 원인</Table.Th>
              <Table.Th style={{ width: 280 }}>대응 가이드</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredList.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#888" }}>
                  일치하는 에러 코드가 없습니다.
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredList.map((item) => (
                <Table.Tr key={item.code}>
                  <Table.Td style={{ textAlign: "center", verticalAlign: "top" }}>
                    <Badge color={getCategoryBadgeColor(item.category)} variant="filled" size="sm">
                      {item.code}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "center", verticalAlign: "top" }}>
                    <Badge color={getCategoryBadgeColor(item.category)} variant="light" size="xs">
                      {item.category}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ fontWeight: 600, verticalAlign: "top" }}>
                    {item.name}
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: "top" }}>
                    <code style={{ fontSize: "0.8rem", color: "#d6336c", background: "#f8f9fa", padding: "2px 4px", borderRadius: 4 }}>
                      {item.exceptionType}
                    </code>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: "top", color: "#343a40" }}>
                    {item.description}
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: "top", color: "#1864ab", fontSize: "0.82rem" }}>
                    {item.actionGuide}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <Flex justify="flex-end" mt="md">
        <Button onClick={onCancel} variant="default" leftSection={<IconX size={16} />}>
          닫기
        </Button>
      </Flex>
    </Modal>
  );
}
